// =====================================================================
//  GAME SETTINGS — the file to tweak.
//  Sizes are in centimeters, like a real table.
//
//  Table directions (from YOUR seat):
//    x  = left / right   (negative = your left, positive = your right)
//    z  = near / far     (positive = closer to you, 0 = middle of the mat)
//  Your opponent's side is the exact mirror, so you only set your side.
// =====================================================================

export const CONFIG = {
  gameTitle: 'Arellix: Online',

  // ---------- Images (files go inside the `public` folder) ----------
  // If a file is missing, a placeholder is drawn instead.
  images: {
    mat: 'mat/mat.png', // one image covering the whole mat (both sides)
    menu: 'menu/menu.png', // the picture behind the main menu (a wide one works best). '' or a missing file = the plain menu with its fan of card backs
    cardBack: 'cards/back.png', // the one card back used by every card
    cardFolder: 'cards/', // card fronts; the file name goes in cards.csv
    tokenFolder: 'tokens/', // token images; the file name goes in tokens.csv
    gerziteBar: '', // optional image for the gerzite bar, e.g. 'mat/gerzite-bar.png'
    darkGerziteBar: '', // optional image for the dark gerzite bar
  },

  // ---------- Card faces ----------
  // Every card is drawn by the game from its row in cards.csv and the card templates in public/cardframes/:
  // its art (the `image` column: a picture in public/art/, moved with art_x / art_y / art_zoom) goes in the
  // window, the frame goes over it, and the name, stats, tiers, description and flavor are printed on it.
  // Change a cell in cards.csv and the card changes with it.
  //   enabled: false = the older look (a full card picture from public/cards/, or the drawn placeholder).
  //   setSize: no longer used: each set's size is in `sets` above. year: the year on the set line.
  //   ink: the color of the name, the type lines and the numbers. A card uses its rarity's color (see
  //        placeholderCards.rarity); common cards use `common`. Its frame's rim is tinted the same color.
  //   styles: the card kinds whose frame is colored their own way rather than by rarity.
  //   lightTextBelow: the description and flavor are printed light (with a dark halo) on any panel darker than
  //        this; on a lighter one they are black, as on the templates. 256 prints every card light.
  //   tint: how strongly a color is laid over the grey template (higher = lighter); leave as is.
  // ---------- Card sets ----------
  // Each set: its name, the words on the set line at the foot of its cards, how many cards it has, the folder its
  // card templates are in (inside cardFaces.folder; a piece that isn't there is taken from the Base Set's), and
  // `from`: the game's own number for its card #1 is from + 1, so two sets can both have a card #1 (Bones &
  // Bruises #1 is card 1001 to the game; its cards show #1 to #22). A card's set is its `set` cell in cards.csv.
  sets: {
    base: { name: 'Base Set', tag: '', line: 'BASE SET', size: 424, frames: '', from: 0 },
    bab: { name: 'Bones & Bruises', tag: 'B&B', line: 'BONES & BRUISES', size: 22, frames: 'bab/', from: 1000 },
  } as Record<string, { name: string; tag: string; line: string; size: number; frames: string; from: number }>, // tag: shown before a card's number in lists (B&B #1)

  cardFaces: {
    enabled: true,
    folder: 'cardframes/',
    artFolder: 'art/',
    setSize: 424,
    year: 2026,
    ink: { common: '#797c84', grand: '#cccccc', ancient: '#13552e' } as Record<string, string>,
    darkGlow: '#7a0a0e', // the glow around the cost gem of a card paid for with Dark Gerzite (cost_type dark)
    cloneTint: '#a9dcff', // a clone on the table (From the Mirror Realm, Spectral Clone…) is tinted this pale spectral blue
    lightTextBelow: 256, // a name plate and text box darker than this (0-255) get light print instead of black (256 = every card's description is light, like the others)
    styles: {
      trigger: { rim: '#371b33', panels: '#af9cb3', ink: '#311d37' },
      summon: { rim: '#bb9568', stone: '#f0ad60', disc: '#dbb78b', ink: '#7a5831' },
    } as Record<string, { rim?: string; stone?: string; panels?: string; disc?: string; ink: string }>,
    tint: { rim: 112, stone: 112, panels: 91 },
    // Emperors: the stone ring around the very edge of the card takes the Emperor's affiliation color (two
    // affiliations: one on each side). `edgeLift` is how light it comes out (1 = the affiliation color itself).
    emperor: { edgeLift: 0.8, gem: { ring: '#646923', disc: '#a9c7c9', discDark: '#6f8f96', hex: '#64691f' } },
  },

  // ---------- Sizes ----------
  // standard poker card, 2.5 x 3.5 in. cornerRadius rounds the corners of cards on the table, like a real
  // playing card: in pixels of a 500-pixel-wide card picture (10 = a small trim, 0 = square corners)
  card: { width: 6.35, height: 8.89, cornerRadius: 10 },
  token: { size: 3 }, // token diameter
  table: { width: 172, depth: 124 },
  mat: { width: 96, depth: 66 }, // whole mat; each side gets half the depth

  // ---------- Where things sit on YOUR side ----------
  zones: {
    deck: { x: -58, z: 22 }, // left of the mat
    discard: { x: 60, z: 22 }, // right of the mat
    emperor: { x: 0, z: 24.36 }, // where your Emperor starts: the middle of the Emperor slot on the mat
    spawn: { x: 0, z: 9 }, // where spawned cards and tokens appear when the camera isn't followed (see `spawning` below)
    fractal: { x: 82, z: 4 }, // where your Fractal stack sits (just right of the gerzite bar)
    opponentHandRow: { z: 42 }, // where your hand's card backs show for your opponent
    gerziteBar: { x: 64, z: 4, width: 29, depth: 6.4, slots: 20, columns: 10 },
    darkGerziteBar: { x: 64, z: 11.2, width: 29, depth: 3.6, slots: 10, columns: 10 },
  },

  // ---------- Where counters, tokens and summoned cards appear ----------
  // followCamera: they appear near the middle of what the player is looking at, so a counter asked for
  //               while zoomed in on Lane 5 turns up by Lane 5. false = always in zones.spawn above.
  // avoidCards:   they never appear on a card (where a counter would attach itself to it): the nearest
  //               free spot is used instead, on the player's side of the card if there is room.
  //               A summoned card also keeps out of every slot until its player puts it in one.
  // gap = cm kept between a new token and any card · step = how finely the table is searched for a free
  // spot · reach = how far from the first spot the search goes before giving up.
  // barSnap: Gerzite or Dark Gerzite brought in while you look within this far (cm) of your resource bars goes onto its bar
  spawning: { followCamera: true, avoidCards: true, gap: 0.5, step: 1, reach: 45, barSnap: 9 },

  // ---------- Slots on the mat ----------
  // Every card slot drawn on the mat: where it is and how big, in cm from the middle of the mat
  // (YOUR side; your opponent's slots are the mirror image). A card whose middle is inside a slot's
  // rectangle is "in" that slot.
  //   effect: an effect played on a card when it is placed there (colors: playEffects)
  //   row:    slots that share a row name close up when one of them empties (the Spell slots); a card
  //           dropped anywhere on the row goes to its first open slot
  //   holds:  how many cards the slot takes (1 = one card at a time)
  //   counts: with holds, only cards of these types are counted and limited (a lane holds one unit,
  //           and a card of another type let in by extra_slots isn't counted). Cards with shares_lane = yes in cards.csv ignore the limit.
  // Which card types may be placed in which slots is the next setting, `slotsFor`.
  slots: [
    { name: 'Lane 1', x: -35.05, z: 11.55, width: 6.87, depth: 9.14, holds: 1, counts: ['troop', 'building'] },
    { name: 'Lane 2', x: -17.12, z: 10.73, width: 6.87, depth: 9.14, holds: 1, counts: ['troop', 'building'] },
    { name: 'Lane 3', x: 0, z: 9.91, width: 6.87, depth: 9.14, holds: 1, counts: ['troop', 'building'] },
    { name: 'Lane 4', x: 17.12, z: 10.73, width: 6.87, depth: 9.14, holds: 1, counts: ['troop', 'building'] },
    { name: 'Lane 5', x: 32.73, z: 11.55, width: 6.87, depth: 9.14, holds: 1, counts: ['troop', 'building'] },
    { name: 'Support', x: -35.06, z: 24.57, width: 6.84, depth: 9.12, holds: 1 },
    { name: 'Minion', x: -17.13, z: 24.59, width: 6.84, depth: 9.14, effect: 'minion', holds: 1 },
    { name: 'Emperor', x: 0, z: 24.36, width: 7.92, depth: 10.99 },
    { name: 'Spell 1', x: 12.07, z: 24.57, width: 6.84, depth: 9.12, row: 'spells', holds: 1 },
    { name: 'Spell 2', x: 21.45, z: 24.57, width: 6.84, depth: 9.12, row: 'spells', holds: 1 },
    { name: 'Spell 3', x: 30.82, z: 24.57, width: 6.84, depth: 9.12, row: 'spells', holds: 1 },
    { name: 'Spell 4', x: 40.2, z: 24.57, width: 6.84, depth: 9.12, row: 'spells', holds: 1 },
    { name: 'Biome', x: 0, z: 0, width: 9.8, depth: 7.1 }, // shared by both players, in the middle of the mat
  ] as { name: string; x: number; z: number; width: number; depth: number; effect?: string; row?: string; holds?: number; counts?: string[]; hand?: number }[],
  // Where each card type belongs. A card can only be placed in one of its type's slots, and nowhere
  // else on the table. "Lane" means any of Lane 1–5 and "Spell" any of Spell 1–4 (a slot's kind is its
  // name without the number). A card with two types (building/troop) may use the slots of both.
  // One card can be let into more slots in cards.csv: extra_slots = biome, or lane/spell, and so on.
  // A type that isn't listed here can go anywhere; `{}` switches the whole rule off.
  slotsFor: {
    troop: ['Lane', 'Minion'],
    building: ['Lane'],
    spell: ['Spell'],
    emperor: ['Emperor'],
    biome: ['Biome'],
    tome: ['Support'],
    trigger: ['Support'],
  } as Record<string, string[]>,
  snapToSlots: true, // a card dropped with its middle inside a slot settles onto the slot's centre
  // Summoned units (the "summon" rows of cards.csv) and clones may share a lane with another unit. A lane
  // holding more than one card lays them out by itself: two side by side at `two` of full size, three or four
  // in a 2 x 2 grid at the same size, more in a 3 x 3 (then 4 x 4) grid, with `gap` cm between them. A card on
  // its own in a lane goes back to full size. Tokens on a card move and shrink with it.
  // A summoned unit dropped on the bottom edge of another card (the bottom `attachBand` of it) is attached
  // there: it shrinks to `attachScale`, sits along that edge and follows the card. Dragging it off detaches it.
  // Swarms: a card with a Count (Skell-Minis ×3) takes the field as that many cards in its lane, one for each unit,
  // all copies of the card played. Each is a Unit of its own (own health, counters, statuses; it can be aimed at on its
  // own, and splash reaches them all), but they act as one: attacking with one sends every other one that can at the
  // same target (the rest must attack too this turn, or are tapped as the Attack Phase ends), sacrificing one
  // sacrifices them all for the Dark Gerzite the whole swarm is worth, and the card goes to its owner's discard pile
  // with the last of them. fractalsPerSwarm: an idle swarm earns one Fractal, not one per unit. split: false = the
  // old way (one card with a Count counter).
  // once: moments a swarm answers once for all of its units (Charred Clan's offer to tap as a Volcanic Unit arrives).
  swarms: { split: true, fractalsPerSwarm: true, once: ['ally_summon'] as string[] },
  sharedLanes: { two: 0.48, gap: 0.25, attachScale: 0.22, attachBand: 0.3 },
  // Cards that make a clone of a Unit: the card's menu lists them under "Clone…". A clone is a spawned copy in
  // the same lane, tinted light blue, that says what made it. health = its full health (blank = the card's
  // own); statuses = it gets copies of the status tokens on the Unit; entrenched = it arrives Entrenched.
  cloneSources: [
    { name: 'From the Mirror Realm', health: 1, statuses: true, entrenched: true },
    { name: 'Spectral Clone', health: 1 },
    { name: 'Illusionist' },
  ] as { name: string; health?: number; statuses?: boolean; entrenched?: boolean }[],
  // In a two-player game, a card played from your hand goes into a slot on YOUR side of the mat only (the
  // slots in `sharedSlots` belong to both). On the solo table you may still play into either side, to set up
  // a practice opponent. Moving a card that is already in play isn't limited. false = either side, always.
  playOwnSide: true,
  sharedSlots: ['Biome'],
  linkedSlots: ['Emperor', 'Minion'], // damage put on a card in one of these is copied onto the card in the other
  linkMirrorsStatus: true, // …and so is a status that comes from your opponent (statuses you give your own cards are not)
  // Players usually place tokens on their own cards themselves, so these harmful statuses always count as
  // coming from the opponent and are copied, whoever puts the token down. (Anything your opponent places by
  // hand on your Emperor or Minion is copied too.)
  // the positive statuses Vacuum keeps off the Units in its lane (tokens.csv effects)
  positiveStatuses: ['damage-boost', 'overheal', 'thorns', 'buffer'],
  hostileStatuses: ['bleed', 'burn', 'comatose', 'decay', 'frozen', 'locked', 'possession', 'stunned', 'susceptible', 'weakness'],

  // ---------- Play settings ----------
  // the table plays the rules itself: outside the solo table nothing is done by hand that only the rules or a card
  // may do (changing or moving counters, moving, turning or flipping played cards, drawing or searching piles, discarding
  // from the hand outside your Overdraw Phase). false = all of that is allowed again, as on the solo table.
  lockHandEdits: true,
  deleteInGames: false, // deleting tokens and summoned cards by hand outside the solo table (false: only the solo table allows it)
  handLimit: 10,
  hoverNames: true, // show the name of the card or token under your cursor (false = off)
  startingHand: 7,
  // The starting hand: at most startingSpells Spell or Support cards (Biomes, Tomes, Triggers), the rest Units (Troops or
  // Buildings); no card with a role in startingNoRoles (a Win Condition) and none costing more than startingMaxCost.
  // A deck that can't make a hand that way loosens the rules in turn: first the Spell/Unit split, then the role and
  // cost limits. (Cards marked starting_hand = no in cards.csv are never dealt into it, whatever the deck.)
  startingSpells: 2,
  startingMaxCost: 8,
  startingNoRoles: ['win condition'] as string[],
  // Card types never dealt into a starting hand (on top of cards marked starting_hand = no in cards.csv):
  // they are passed over as the hand is dealt, stay in the deck, and can be drawn later. [] = any type.
  noStartingHand: [] as string[],
  // A summoned unit attached to one of these cards (dropped on its bottom edge) gets these tokens put on it.
  // `units` limits it to those summoned units (left out = any). Token ids from tokens.csv.
  attachGains: [{ parent: 'Skeleton Dropship', units: ['Skell-Mini'], tokens: ['ambush'] }] as { parent: string; units?: string[]; tokens: string[] }[],
  // The end of a game: how long the table waits before the end screen comes in (seconds), so the last blow lands
  // first (afterFall: an Emperor fell; afterOther: a forfeit or a called game), and how long the table darkens before
  // it; then confetti over a win (colors, how many burst out at once, how long more keep falling), embers over a loss
  endScreen: {
    afterFall: 2.8,
    afterOther: 0.9,
    dusk: 1.1,
    confettiColors: ['#d9a441', '#ffd76a', '#84b8d0', '#3fa9f5', '#9a78d4', '#ebe6da', '#e0675a'],
    confettiBurst: 140,
    confettiSeconds: 4.5,
    emberColors: ['#7a1206', '#b3261e', '#e0675a'],
  },
  startingGerzite: 10, // gerzite tokens each player starts with, placed on their gerzite bar
  // How a card lands when played from your hand.
  // Rotations: 0 = forward, facing you (active) · 1 = sideways (inactive) ·
  //            2 = backward, facing your opponent (Entrenched)
  arrival: {
    // Only these card types can be Entrenched or have Ambush. Every other type (spells,
    // biomes, triggers, tomes…) simply arrives facing you. Use 'all' to include every type.
    types: ['troop', 'building'] as 'all' | string[],
    entrenchedRotation: 2, // how those cards normally arrive
    ambushRotation: 0, // …and how they arrive with arrival = ambush in cards.csv
    // Card types that always go face-down when played from your hand (flip them with F later).
    // Empty the list ([]) to turn this off.
    faceDownTypes: ['trigger'] as string[],
    // Card types that lie sideways while in play and are never tapped or untapped (the shared Biome
    // slot is sideways on the mat). For these, sideways means "in play"; flip one if something happens to it.
    sidewaysTypes: ['biome'] as string[],
  },

  // ---------- What the table does for you each turn ----------
  // Set a number to 0 (or a switch to false, or empty a list) to turn that helper off.
  turn: {
    // When cards can be played from your hand. Every card type can be played in your Summon Phase. After
    // that only Spells can, in your Attack Phase, since a Spell may be what boosts an attack. Phases are
    // numbered 0 Overdraw, 1 Summon, 2 Attack, 3 End. A free play (Ctrl / ⌘, for a card effect that puts a
    // card into play at another time) ignores this. `enabled: false` = any card, any time.
    playPhases: { enabled: true, phases: [1], byType: { spell: [1, 2] } as Record<string, number[]> },
    // When a unit can be sacrificed for Dark Gerzite: on your own turn, in these phases (0 Overdraw,
    // 1 Summon, 2 Attack, 3 End). `phases: []` = any phase; `ownTurnOnly: false` = on either player's turn.
    sacrifice: { ownTurnOnly: true, phases: [1, 2] as number[] },
    payToPlay: true, // playing a card from your hand takes its Gerzite cost off your bar (hold Ctrl / ⌘ as you drop it to play it free)
    // A Darkgale summon: the card is paid for with Dark Gerzite instead of Gerzite, and the Darkgale token
    // is put on it. `multiplier` is how many times its cost a card type pays (Spells cost double).
    // Set token to '' to switch Darkgale summons off.
    darkgale: { token: 'darkgale', multiplier: { spell: 2 } as Record<string, number> },
    gerziteAtStart: 2, // Gerzite gained when your turn begins (Overdraw Phase)…
    gerziteAtEnd: 2, // …and when you end your turn
    gerziteOnOpeningTurn: false, // does the player who goes FIRST also gain it on the game's very first turn? (no: blue starts on 10, red reaches 12)
    untapAtStart: true, // your sideways (tapped) cards turn back to face you when your turn begins
    overdrawCost: 1, // Gerzite paid for each Overdraw (drawing past 7 in your Overdraw Phase); 0 = never ask
    passiveDrawTo: 7, // ending your turn draws you up toward this many cards…
    passiveDrawMax: 2, // …but never more than this many at once
    deckOutDamage: 1, // damage to your Emperor for each card the end-of-turn draw could not give you
    emperorEntrenched: 2, // Emperors start the game with this many turns of Entrenched
    // Counters that count turns go down by 1 by themselves, on cards owned by the player whose turn it is,
    // and are removed at 0. (Counters that hold an amount, like Buffer, Thorns, Damage Boost or Weakness,
    // are left alone.) Names are token effects from tokens.csv.
    tickDown: {
      start: ['overheal', 'timer-start'], // as your turn begins
      summon: ['bleed', 'entrenched'], // on entering your Summon Phase (after Overdraw)
      attack: [] as string[], // on entering your Attack Phase
      end: ['frenzy', 'divinity', 'overclock', 'overshield', 'frozen', 'possession'], // on entering your End Phase (Possession: the End Phase of the player controlling the card) (Regeneration has a turn clock instead: see statusActs)
      turnEnd: ['timer-end'], // at the moment you end your turn
    } as Record<'start' | 'summon' | 'attack' | 'end' | 'turnEnd', string[]>,
    // Turn timers: counters you set to any number of turns. Unlike the status counters above they also count
    // when they are NOT on a card (then they follow the turns of whoever put them down), a Frozen card
    // doesn't pause them, and both players are told when one runs out.
    timers: ['timer-start', 'timer-end'] as string[],
    // Cards that stay in play for a set number of their owner's turns (the `turns` cell in cards.csv).
    // One of these arrives with `timer` on it, set to its turns. When a turn timer runs out on such a card,
    // `expiredToken` is put on it: the card is no longer active (the token greys it, and its aura stops).
    // Nothing is discarded for you. `types: []` switches this off.
    // Which timer it gets: the card's turns cell can say ("3 start", "3 end"); a bare number gets `timer`.
    //   an End of Turn Timer counts the turn the card was played as the first (it ticks as that turn ends);
    //   a Start of Turn Timer starts counting on its owner's next turn, so "3 start" on a Spell that hampers
    //   your opponent (Fog) lasts through three of their turns.
    // Paying to take one out of play, or to keep one going:
    //   removable: card types either player may remove from play by paying the card's removal_cost (Biomes).
    //              One whose turns are over is cleared away for nothing. Playing a new one into the same slot
    //              asks for the old one's removal cost first.
    //   restartable: card types whose owner may pay the card's replay_cost once its turns are over (Tomes).
    //              Each restart lasts one turn less than the run before it; one that would last 0 can't be bought.
    removable: ['biome'] as string[],
    restartable: ['tome'] as string[],
    discardSpentTomes: true, // a Tome (restartable card) whose turns are over goes to the discard pile as its owner's turn ends, unless they restarted it that turn
    //   countUp: the timer a card gets when its turns cell says "8 up" (Arcane Charge). It starts at 0 and
    //              gains 1 as each of its owner's turns begins, stopping at the card's number. One put on the
    //              table by hand counts up too, without a stop. `false` for enabled = it never moves by itself.
    //   endings:   cards that end by an event instead of a turn count (the `brings` and `ends` cells in
    //              cards.csv). enabled: false = no card brings a token or ends by itself. rollEnds = the die
    //              faces that end an "opponent_rolls_even" card (Pestilence Approaching).
    countUp: { enabled: true, token: 'timer-up' },
    endings: { enabled: true, rollEnds: [2, 4, 6] as number[] },
    //   unsetTimers: a card whose turns cell has a "?" for the number ("? start") still arrives with its turn
    //              timer, showing "?". It does not count down, and the card never runs out, until its owner
    //              clicks the timer to give it a number. false = such a card gets no timer at all.
    //   unsetReminder: the owner is reminded as they enter their End Phase while one is still showing "?".
    lifetime: {
      types: ['biome', 'tome', 'spell'] as string[], timer: 'timer-end', timers: { start: 'timer-start', end: 'timer-end' } as Record<string, string>, expiredToken: 'destroyed',
      unsetTimers: true, unsetReminder: true,
    },
    // The little clock that appears over a turn timer each time it counts down: a clock face whose hand
    // sweeps round once. seconds = how long it shows, size = how big, height = how far above the timer,
    // sweeps = how many times the hand goes round. `enabled: false` = no clock.
    tickClock: { enabled: true, seconds: 1.3, size: 3.2, height: 2.6, sweeps: 1, color: '#bfe9ff' },
    // What status tokens do by themselves on the turn player's own cards. `when` is the same four moments
    // as tickDown, and happens just before that moment's tick-down.
    //   damage: adds damage to the card (amount: a number, or 'value' = the number on the token);
    //           thenDrop lowers the token by that much afterwards (Decay: 5, then 4, then 3…)
    //   heal:   takes that much damage off the card
    //   notice: only pulses the token and tells you, for things you settle yourselves
    //   then:   with damage, what the card's owner must do next; they are told as it happens
    //   cure:   with damage, the card's owner is then asked to roll a die: these faces take the token off
    //           (Burn: an even number cures it). The turn can't move on until the die has been rolled.
    //   text:   with notice, the line shown to the card's owner ({card} = the card's name)
    //   choose: the moments its player may pick between, on the counter itself (click it): it acts then
    //           instead of at `when` (Regeneration: as your turn begins, or as it ends)
    //   clocked: its turn clock (see durations) goes down right after it acts, at that same moment, not at
    //           durations.when. Its number is how much it does each time; the clock is how many turns
    //           (Regeneration 2 with a 3-turn clock heals 2 a turn for 3 turns). No clock = until removed.
    statusActs: [
      { effect: 'bleed', when: 'start', does: 'damage', amount: 1 },
      { effect: 'decay', when: 'attack', does: 'damage', amount: 'value', thenDrop: 1 },
      { effect: 'regeneration', when: 'end', does: 'heal', amount: 'value', choose: ['start', 'end'], clocked: true },
      { effect: 'burn', when: 'attack', does: 'damage', amount: 1, cure: [2, 4, 6] },
    ] as { effect: string; when: 'start' | 'summon' | 'attack' | 'end'; does: 'damage' | 'heal' | 'notice'; amount?: number | 'value'; thenDrop?: number; then?: string; cure?: number[]; text?: string; choose?: ('start' | 'end')[]; clocked?: boolean }[],
    // Turn clocks. The counters in `effects` hold an AMOUNT (Weakness 2, Shield 3), so their number can't also
    // count turns. Each of them can carry a small clock instead: how many turns it lasts. The clock goes down
    // by 1 at `when` (the same four moments as tickDown) of the turn of the player whose card it is on, and at
    // 0 the counter is taken off. No clock = it stays until someone removes it.
    //   max: the most turns a clock can be set to
    //   startsNextTurn: true = a clock set during its card owner's own turn doesn't count that same turn
    //   clock: how the little clock on the counter looks (size and offset are fractions of the counter's width)
    // The same counters can also carry a wear-off timer instead (or as well): part of their number is temporary
    // and wears off 1 at a time as each of its card owner's turns begins (`drainWhen`), down to the counter's own
    // number (Thorns 1, +2 from Cagurn's Razor Point = 3; next turn 2; then 1, and the timer is gone).
    durations: {
      enabled: true,
      when: 'end' as 'start' | 'summon' | 'attack' | 'end',
      drainWhen: 'start' as 'start' | 'summon' | 'attack' | 'end', // when a wear-off timer takes its 1 off (see below)
      effects: ['weakness', 'susceptible', 'damage-boost', 'buffer', 'thorns', 'shield', 'range', 'regeneration'] as string[],
      max: 20,
      startsNextTurn: false,
      clock: { size: 0.5, offset: 0.33, face: '#f4efe1', rim: '#1d1f23', hand: '#b3261e', last: '#ffc27a' },
      // the wear-off badge on a counter part of whose number is temporary: it sits on the counter's other corner
      drain: { face: '#dfeefa', rim: '#1d1f23', arrow: '#2f6fe0' },
    },
    // The die that is rolled to cure a status (see `cure` in statusActs). It is one of the real dice from
    // tokens.csv, in the roller's own color: `first` for the player who went first (blue), `second` for the
    // other (red). It is thrown onto the table just in front of the card and stays there; the same die is
    // picked up again for that player's next cure roll. gap = cm between the card's edge and the die.
    // the die a cure roll (Burn) is thrown with: blue for whoever went first, red for the other player. It fades
    // away by itself `fadeAfter` seconds after it lands, over `fadeTime` seconds, and comes back for the next roll.
    cureDie: { first: 'die-blue', second: 'die-red', gap: 0.9, fadeAfter: 2.5, fadeTime: 0.7 },
    frozenPauses: true, // while a card is Frozen, its other status tokens neither act nor count down
    // SP decay: as you END your turn, each of your face-up Buildings loses `amount` SP. It is added to the blue
    // SP Decay counter on the Building (one is put there the first time). Only the tokens in `stoppedBy`
    // hold it off: an Entrenched Building doesn't decay, but a Frozen or Stunned one still does. A token in
    // `more` changes the amount while it is on the Building (Crumble: 3 instead of 2). A Building never
    // decays below 0 SP. `enabled: false` leaves decay to the players.
    spDecay: { enabled: true, amount: 2, stoppedBy: ['entrenched', 'destroyed'] as string[], more: { crumble: 3 } as Record<string, number> },
    buildingsShedOverheal: true, // Buildings lose all their Overheal as their owner's turn begins
    // Damage from a status effect (Bleed, Burn, Decay) goes straight to a card's health, past its Overheal,
    // and a card that is below full health can't keep Overheal: the Overheal token is removed.
    statusDamageEndsOverheal: true,
    // An Effect Draw with a full hand: the card can't stay in your hand. You either discard it and gain
    // this much Gerzite, or put it on the bottom of your deck and gain this many Fractals.
    // (enabled: false = a full hand simply can't draw.)
    effectDrawOverflow: { enabled: true, discardGerzite: 1, bottomFractals: 1 },
    // Fractals offered in your End Phase: one for each of your face-up Troops and Buildings that is not
    // tapped, not in the Minion slot, and has none of these tokens on it.
    fractalsAtEndTurn: true, // if you end your turn without collecting them, the table collects them for you
    fractalBlockers: ['destroyed', 'entrenched', 'bleed', 'burn', 'comatose', 'crumble', 'decay', 'frozen', 'locked', 'possession', 'stunned', 'susceptible', 'weakness'],
  },

  // ---------- Triggers ----------
  // A sprung Trigger pays its owner its own Gerzite cost plus this much, then goes to the discard pile.
  trigger: { payoutBonus: 2 },

  // ---------- Overtime ----------
  // ---------- 2v2 (Team Versus) ----------
  // Four players, two teams. Each side of the mat has two players: the left one plays Lanes 1–2 and the right
  // one Lanes 3–4; each has their own Emperor, Minion, Support and 3 Spell slots. The Biome slot is shared.
  // Teammates share one Gerzite bar (20) and one Dark Gerzite bar (10). A team's turn is its left player's
  // turn, then its right player's; then the other team's. Gerzite comes once per team turn.
  teams: {
    startingGerzite: 16, // on the team's shared bar
    gerziteAtStart: 2, // as the team's turn begins (the team that goes first doesn't get it on the very first turn)
    gerziteAtEnd: 4, // as the team's turn ends
    handoffGerzite: 1, // as the first teammate passes the turn to the second, within the team's turn (so the second player has more to play with)
    overtimeAfter: 8, // Overtime begins once everyone still in the game has finished this many turns
    overtimeAtStart: 4, // Gerzite during Overtime, at the start…
    overtimeAtEnd: 6, // …and at the end of each team turn
    // The 2v2 table. The same settings as the 1v1 table above (mat, slots, zones…), used in place of them for
    // a 2v2 game. Positions are for the LEFT player of the near side; the right player's are mirrored left to
    // right, and the far side's are the mirror image as usual. A slot's `hand` says whose half it is in:
    // 1 = the left player's, -1 = the right player's (no hand = shared, the Biome).
    layout: {
      images: { mat: 'mat/mat-2v2.jpg' },
      mat: { width: 136, depth: 66 },
      table: { width: 236, depth: 132 },
      zones: {
        deck: { x: -77, z: 12 },
        discard: { x: -77, z: 25 },
        emperor: { x: -38, z: 24.36 },
        spawn: { x: -34, z: 3 },
        fractal: { x: 110, z: 5 }, // the team's Fractal stack: just right of its shared Gerzite bar
        opponentHandRow: { z: 42 },
        // the team's shared bars, on the right of its side
        gerziteBar: { x: 97, z: 5, width: 16, depth: 12.8, slots: 20, columns: 5 },
        darkGerziteBar: { x: 97, z: 16.5, width: 16, depth: 6.4, slots: 10, columns: 5 },
      },
      slots: [
        { name: 'Lane 1', x: -51, z: 11.55, width: 6.87, depth: 9.14, holds: 1, counts: ['troop', 'building'], hand: 1 },
        { name: 'Lane 2', x: -17, z: 10.73, width: 6.87, depth: 9.14, holds: 1, counts: ['troop', 'building'], hand: 1 },
        { name: 'Lane 3', x: 17, z: 10.73, width: 6.87, depth: 9.14, holds: 1, counts: ['troop', 'building'], hand: -1 },
        { name: 'Lane 4', x: 51, z: 11.55, width: 6.87, depth: 9.14, holds: 1, counts: ['troop', 'building'], hand: -1 },
        { name: 'Support', x: -60, z: 24.57, width: 6.84, depth: 9.12, holds: 1, hand: 1 },
        { name: 'Minion', x: -49.5, z: 24.59, width: 6.84, depth: 9.14, effect: 'minion', holds: 1, hand: 1 },
        { name: 'Emperor', x: -38, z: 24.36, width: 7.92, depth: 10.99, hand: 1 },
        { name: 'Spell 1', x: -26.5, z: 24.57, width: 6.84, depth: 9.12, row: 'spells-left', holds: 1, hand: 1 },
        { name: 'Spell 2', x: -17.5, z: 24.57, width: 6.84, depth: 9.12, row: 'spells-left', holds: 1, hand: 1 },
        { name: 'Spell 3', x: -8.5, z: 24.57, width: 6.84, depth: 9.12, row: 'spells-left', holds: 1, hand: 1 },
        { name: 'Spell 1', x: 26.5, z: 24.57, width: 6.84, depth: 9.12, row: 'spells-right', holds: 1, hand: -1 },
        { name: 'Spell 2', x: 17.5, z: 24.57, width: 6.84, depth: 9.12, row: 'spells-right', holds: 1, hand: -1 },
        { name: 'Spell 3', x: 8.5, z: 24.57, width: 6.84, depth: 9.12, row: 'spells-right', holds: 1, hand: -1 },
        { name: 'Emperor', x: 38, z: 24.36, width: 7.92, depth: 10.99, hand: -1 },
        { name: 'Minion', x: 49.5, z: 24.59, width: 6.84, depth: 9.14, effect: 'minion', holds: 1, hand: -1 },
        { name: 'Support', x: 60, z: 24.57, width: 6.84, depth: 9.12, holds: 1, hand: -1 },
        { name: 'Biome', x: 0, z: 0, width: 9.8, depth: 7.1 },
      ] as { name: string; x: number; z: number; width: number; depth: number; effect?: string; row?: string; holds?: number; counts?: string[]; hand?: number }[],
      camera: {
        start: { x: 0, y: 128, z: 118, lookAtZ: 22 },
        topDown: { height: 235, z: 14 },
        bounds: { x: 130, yMin: 8, yMax: 260, zMin: 4, zMax: 160 },
      },
    },
  },

  // ---------- Missing an attack ----------
  // When a Unit or Emperor draws an attack arrow at a card, the table works out its chance to miss from every
  // card in play that gives one, and rolls for it (a die when the chance is in sixths). The result is shown and
  // logged; on a miss no damage goes on (a bot leaves its damage off; a player simply doesn't put any on).
  // Several chances together: it misses if any one of them does. Each line names the card that gives it:
  //   by       'opponents' = attacks made against the card's owner's side (Fog, Smog); 'all' = everyone's (Biomes)
  //   units / emperors   the chance for an attacking Unit / an attacking Emperor
  //   affiliations       only attackers of these affiliations (left out = all); notAffiliations: all but these
  //   target   the chance applies to attacks AT this card (Ash Druid), only of these target types
  // neverMiss: attackers that can't miss (a card name, or { affiliation, card } for "while that card is in play")
  misses: {
    enabled: true,
    sources: [
      { card: 'Fog', by: 'opponents', units: 1 / 3 },
      { card: 'Smog', by: 'opponents', units: 2 / 3, emperors: 1 / 3 },
      { card: 'Hailstorm', by: 'opponents', units: 1 / 3 },
      { card: 'Dense Woodland', by: 'all', units: 1 / 6, notAffiliations: ['forested'] },
      { card: 'Muddy Creek Bed', by: 'all', units: 1 / 3, emperors: 1 / 3, affiliations: ['volcanic', 'forested'] },
      { card: 'Ash Druid', target: ['target', 'pierce'], units: 1 / 4, emperors: 1 / 4 },
    ] as { card: string; by?: 'opponents' | 'all'; units?: number; emperors?: number; affiliations?: string[]; notAffiliations?: string[]; target?: string[] }[],
    // (given: only the Unit that card gave its status to, while that card is active: Enhanced Vision's Troop)
    neverMiss: [{ card: 'Sureshot Bowman' }, { affiliation: 'submerged', while: 'Muddy Creek Bed' }, { while: 'Enhanced Vision', given: true }] as { card?: string; affiliation?: string; while?: string; given?: boolean }[],
    // cards that leave a miss chance behind on the Units in the lane in front of them, for their opponent's
    // next turn: as they arrive (Drifts Tarragon) or as they are destroyed (Ash Beetle)
    laneMarks: [
      // looks: played on the card as it does this (from), and on each Unit it marks (on); lingers: a look stays on
      // those Units while the chance lasts (abilityEffects.ambient.marks.blinded)
      { card: 'Drifts Tarragon', when: 'arrive', chance: 1 / 3, looks: { from: 'sand-gust', on: 'sand-blind' }, lingers: true },
      { card: 'Ash Beetle', when: 'destroyed', chance: 2 / 3 },
    ] as { card: string; when: 'arrive' | 'destroyed'; chance: number; looks?: { from: string; on: string }; lingers?: boolean }[],
  },

  // Magical Coin (on_play = coin_gerzite): its caster rolls a die as it is played. Even (heads): the caster gains
  // `heads` Gerzite; odd (tails): their opponent gains `tails`.
  coinGerzite: { heads: 2, tails: 1 },
  // Heart of Limbo (while_in_play = limbo_discards): when it ends, each player draws the top card of
  // their reshuffled discard pile if they have fewer than this many cards in hand.
  limbo: { drawUnder: 10 },
  // Cosmic Beyonder's Entrance (on_play = discard_recycle): how many cards go from the discard pile into the deck.
  discardRecycle: { toDeck: 4 },

  overtime: {
    afterTurns: 12, // Overtime begins once both players have finished this many turns (12 global turns)
    gerzite: 3, // Gerzite gained at the start AND at the end of each turn during Overtime
    emperorDamage: 1, // damage every Emperor takes at the end of every turn during Overtime
    // Double Overtime: from this global turn on, to hasten games that have run this long, the damage goes up to
    // doubleDamage and the Gerzite to doubleGerzite at the start and doubleGerzite at the end of each turn (1v1)
    doubleFrom: 20,
    doubleDamage: 2,
    doubleGerzite: 4,
    ember: '#ff7a2e', // the color of the Overtime glow and embers
  },

  // ---------- Battle Timer (1v1 and 2v2 online) ----------
  // A player asks for it from the timer button at the top of the table; everyone on the other side has to agree
  // (bots always do), and turning it off again works the same way. While it is on, each player has `seconds` for
  // each of their turns, fresh each time the turn comes back to them; at `warnAt` seconds left they are warned,
  // and at 0 (if they haven't passed the turn on) they lose by forfeit. Bots' turns aren't timed.
  battleTimer: {
    seconds: 90,
    warnAt: 30,
  },
  // A player who leaves an online game part-way (closes the page, loses their connection, or leaves the table) has
  // this long to come back before they lose by forfeit (2v2: their team does). Against a bot, the bot wins.
  reconnectSeconds: 120,

  // ---------- Attack arrows and pings ----------
  // Alt + drag from one of your cards draws an arrow both players see; Alt + click flashes a marker.
  pointers: {
    arrowSeconds: 3, // how long an attack arrow stays before fading away
    pingSeconds: 1.6, // how long a ping lasts
  },

  // ---------- Attacks ----------
  // Click one of your units, then the card it attacks: the table deals the damage by the rules (Shield, Buffer,
  // Thorns, Entrenched halving, Weakness, Susceptible, chances to miss…) and taps the attacker.
  // enabled: false = the old table, where damage counters are put on by hand.
  combat: {
    enabled: true,
    // a card with one of these on it can't attack or use its Tap ability
    blockers: ['entrenched', 'frozen', 'stunned', 'comatose', 'locked', 'destroyed'] as string[],
    overhealFirst: true, // damage comes off a card's Overheal first; only what is left over goes onto its health
    emperorFallEnds: true, // an Emperor brought to 0 health ends the game at once (2v2: once a team has none left); both at once: see decideEven
    halvedBy: ['entrenched', 'frozen'] as string[], // a card with one of these takes half of each hit, rounded up
    darkgaleDeals: 2, // a Darkgale unit's damage is multiplied by this…
    darkgaleTakes: 2, // …and so is each hit it takes (which cancels Entrenched's half)
    // a Troop or Building destroyed in a fight leaves the table by itself, to its owner's discard pile.
    // false = it only gets its Destroyed skull, and its owner discards it.
    autoDiscard: true,
    // Cards whose Tap ability is what they do when they attack (Firelance's first shot, Battering Ram, Dark Gerzite
    // Titan's payment…): clicking one offers only Attack, not a separate "Use Tap ability" that would do the same.
    tapIsAttack: ['Firelance', 'Battering Ram', 'Sunken Soldier', 'Cannoneer', 'Dark Gerzite Titan'] as string[],
    firstTurnSpells: false as boolean, // may a Spell be cast on a player's first turn? (the rules say no; a free play, Ctrl / ⌘, still can)
    // What an attack looks like. The arrow shows for `arrowSeconds` (so your opponent sees who attacks whom),
    // then it goes and something flies from the attacker to its target for `flightSeconds`: what it is depends on
    // the attacker's affiliation (the shapes are in src/client/table/attackFx.ts; these are its colors, in order).
    attackFx: {
      enabled: true,
      arrowSeconds: 0.55,
      flightSeconds: 0.5,
      missArrowSeconds: 1.4, // a missed attack: its arrow stays a little longer, and what flies sails wide
      size: 1.7, // how big it all is (1 = small)
      height: 1.6, // how far above the table it travels
      ringSeconds: 0.55, ringSize: 11, // the ring that spreads where it lands
      colors: {
        neutral: ['#c9ced6', '#eef1f5'], // slash, sparks
        undead: ['#0b0a0f', '#7d5bd0', '#3a3a44'], // orb, its rim and sparks, smoke
        spectral: ['#b9f5c8', '#e9fff0'], // energy, wisps
        magical: ['#a34bff', '#e2b8ff'], // glow, sparks
        forested: ['#4fa83a', '#9bd04a', '#7b6a55', '#8f8f8f'], // leaves, lighter leaves, rock, stones
        submerged: ['#3f9ae8', '#dff3ff'], // water, foam
        duned: ['#d9b877', '#c9a45c', '#f0dca8'], // the swirl, dust, grains
        volcanic: ['#7a1408', '#ff8a1e', '#3b3531'], // rock (its glow is the second color), embers, smoke
        frigid: ['#bfe9ff', '#e8f8ff', '#ffffff'], // ice, shards, snow
        astral: ['#0c1650', '#8a4dd6', '#8c8a98', '#d8c8ff'], // the singularity, purple clouds, grey clouds, specks of light
        deckOut: ['#160f1c', '#7a2440', '#efe2c4', '#3b3044'], // Deck-Out: a ragged dark mote with pale torn pages, flying from the empty deck to its Emperor
        remaining: ['#050203', '#e0121f', '#ff5a44', '#3a0208'], // The Remaining: a black core, deep red tendrils, red sparks, dark red smoke
      } as Record<string, string[]>,
    },
    // What a Spell looks like as it reaches the Units and Emperors it is aimed at (damaging them or not): it starts at
    // the Spell card and travels to each target, in the Spell's affiliation's style (the looks are in
    // src/client/table/attackFx.ts, SPELL_STYLES). Spells aimed at a player (looking at a hand) show nothing.
    spellFx: {
      enabled: true,
      delay: 0.25, // seconds after the Spell is played before it sets off
      flightSeconds: 0.8, // how long it takes to get there
      volleyGap: 0.16, // a Spell that strikes again and again (Piercing Arrows): seconds between one strike setting off and the next
      hopSeconds: 0.45, // a Spell jumping from one Unit to the next (Chain Lightning): how long each jump takes
      stormSeconds: 1.0, // Submerged: how long the thunderstorm gathers over the target before the lightning strikes
      most: 8, // at most this many targets shown for one Spell at a time
      colors: {
        neutral: ['#d8c9a8', '#f2ead8', '#8a7a5c'], // arrow, dust, wood chips
        undead: ['#120d14', '#c0392b', '#5d5f66', '#62d26f'], // the dark orb, red, grey smoke, green
        magical: ['#9b3dff', '#ff7fd8', '#f3d6ff'], // purple, pink, white sparks
        spectral: ['#b9f5c8', '#e9fff0', '#7fd8a8'], // ghostly energy, wisps, deeper green
        duned: ['#d9b877', '#c9a45c', '#f0dca8'], // the swirl, dust, grains
        forested: ['#9a6a3c', '#6e4a2a', '#7fae48', '#8a7558'], // roots, darker roots, leaves, dirt
        submerged: ['#2c303b', '#e4f6ff', '#7fc4ff', '#4a5266'], // storm cloud, lightning, rain, lighter cloud
        volcanic: ['#ff4a12', '#ffb03a', '#3b3531', '#7a1408'], // lava, embers, smoke, dark crust
        frigid: ['#a8dcff', '#e8f8ff', '#ffffff'], // freezing wind, frost, snow
        astral: ['#ff7a1e', '#ffc04a', '#c77dff', '#4a2a1a', '#3a2c4a'], // flame, hot glow, purple sparkles, the meteor's rock, smoke
      } as Record<string, string[]>,
    },
    // a summoned unit that rises up out of the table (summon … emerge: Leviathan Body Segment), in a ripple of water
    emerge: { seconds: 1.2, depth: 6, tilt: 0.5, colors: ['#3f9ae8', '#dff3ff'] }, // (depth: how far under the table it starts, in cm; tilt: how steeply it comes up)
  },

  // ---------- Tiers ----------
  // Stacking copies of the same card merges them: 2 copies = Tier II, 3 = Tier III.
  // These are the ids (from tokens.csv) of the token placed on a merged card.
  tierTokens: { 2: 'tier-2', 3: 'tier-3' } as Record<number, string>,
  // What tiers a card has and what each one gives. A card's highest tier comes from its type: Troops,
  // Buildings and Biomes go to Tier III, Spells to Tier II (a Spell that costs 0 has none), every other type
  // has none, and neither do summoned units (number "summon"). The `tiers` column in cards.csv can only
  // lower this (1 = this card never merges). Bonuses are worked out from the card's own cells, and a
  // fraction always rounds UP: 20% of 6 is 1.2, so +2.
  tierRules: {
    maxByType: { troop: 3, building: 3, spell: 2, biome: 3 } as Record<string, number>,
    healthPercent: 20, // Troops and Buildings at Tier II (and III): this much more health, or SP for a Building
    damagePercent: 20, // Troops and Buildings at Tier III: this much more damage (on top of Tier II's health)
    spellRefund: 1, // a Spell merged up to Tier II gives its owner this much Gerzite back (its cost, 1 less)
    biomeRemoval: { 2: 1, 3: 2 } as Record<number, number>, // a Biome's removal cost at each tier: this much more in total
  },

  // ---------- Flying cards ----------
  // Cards with movement = flying in cards.csv hover above the table and bob gently.
  flying: { height: 1.9, bob: 0.24 },

  // ---------- Token rules ----------
  // The few things tokens do by themselves. Names here are token "effect" names (tokens.csv).
  // A card whose while_in_play cell says tap_harvest (Gerzite Farmer) gains `perTap` on its Harvest counter
  // the first time it is tapped each turn (the counter is put on it the first time). Right-click the counter
  // to spend it: each line of `spend` is "this many Harvest for that many of a bar token".
  // token = the counter's id in tokens.csv ('' switches all of this off).
  // Passive effects (a card's "Passive:" line) can't happen while their card carries one of these (the rules:
  // Entrenched, Comatose, Frozen, Locked or Stunned). The table holds back the effects it does by itself:
  // Gemstone Hoarder's steal (a Passive), and Gerzite Farmer's Harvest (a Tap effect, held back the same way).
  // A card with the "passive" ability tag pulses deep red instead of its usual color while one is on it.
  passiveStoppedBy: ['entrenched', 'comatose', 'frozen', 'locked', 'stunned'] as string[],
  // The secret rares (cards.csv 425 to 427): Calling Pillar, Realm Beyond Comprehension and The Remaining.
  // What each one does is switched on by tags in its sheet row (on_play: lane_damage, field_wipe ·
  // while_in_play: untouchable, calling, remaining_gate, jumps, needs_gate); the numbers are here.
  secretRares: {
    laneDamage: 2, // Calling Pillar's Entrance: this much damage to every Unit in its own lane (not the opposing lane across from it)
    callDelay: 3.6, // a Pillar in every lane: seconds the eruption plays before the hand is discarded, The Remaining comes and the Pillars go
    callGerzite: 10, // a Pillar in every lane: its owner gains this much Gerzite (and the rest: see checkCalling in engine.ts)
    // how many Calling Pillars the call takes: one in each lane, and at least this many in all. The 1v1 table has five
    // lanes; the 2v2 table has four a side, so there one lane needs a second Pillar (a player may put theirs in their
    // teammate's lanes too)
    callingPillars: 5,
    wipeDamage: 100, // The Remaining's Entrance: this much damage to every other Troop and Building on both sides
    wipePierce: true, // …as Pierce damage (a Shield doesn't stop Pierce; Overshield does). false = Shields stop it too
    // Packs: none of the three can come from the last packs of a deck (noSecretsFrom: the 5th pack on, counting the
    // Emperor Pack). Pulling any one of them fills the deck with the whole set, these many copies of each in all
    // (counting the one pulled), plus one more card from that pack, and that uses up one of the deck's pack openings.
    autofill: { jumps: 1, remaining_gate: 2, calling: 7 } as Record<string, number>,
    noSecretsFrom: 5,
  },
  harvest: {
    token: 'resource',
    perTap: 1,
    spend: [
      { cost: 2, gives: 'gerzite', amount: 1 },
      { cost: 3, gives: 'dark-gerzite', amount: 1 },
    ] as { cost: number; gives: string; amount: number }[],
  },
  tokenRules: {
    // Link counters: put one on each of two cards with the SAME number and the two are linked like your Emperor
    // and Minion: damage on one is copied onto the other, and so are statuses from the opponent. Bringing in a
    // Link brings in a pair with the next free number. Right-click one to have its card take half the damage.
    linkToken: 'link',
    // Lanespanners: a Troop with this descriptor and a Count is one unit per lane. The card from your hand goes
    // in an empty lane as the middle unit; its owner then chooses which lanes either side the others go in (they
    // may go into lanes already taken). Each unit carries a `lanespannerToken`; the extra units are copies that
    // are deleted (not discarded) once they are cleared away. No Count counter is put on them.
    lanespanTrait: 'Lanespanner',
    lanespannerToken: 'lanespanner',
    // Health counters: health a card has gained. It raises the card's full health (and what is left of it).
    healthToken: 'health',
    // Splash tokens: lay one in a lane, and any counter or status dropped on top of it lands on EVERY unit in
    // that lane instead (damage is dealt to each, a status is put on each). The dropped counter is used up;
    // the Splash token stays for the next one. On a unit with a Count, splash damage reaches each of its units.
    splashToken: 'splash',
    entrenchedToken: 'entrenched', // placed on a troop or building when it arrives Entrenched
    ambushToken: 'ambush', // placed on a troop or building that arrives with Ambush
    // Ambush is a "this turn" thing: its token comes off the moment the card is tapped (it has been used),
    // and any Ambush token still on the table when a turn ends goes with that turn.
    ambushEndsOnTap: true,
    ambushEndsWithTurn: true,
    fractalsPerGerzite: 5, // this many Fractals merge into…
    fractalGives: 'gerzite', // …one of this token (an id from tokens.csv)
    // Tokens that show a small shockwave on a card when they land on it or their number goes up.
    // Add 'range', 'shield' or 'thorns' here if you want those to do it too.
    damageTokens: ['damage'] as string[],
    // What a card's close-up (right-click) says in plain words about a token that is on it. {n} is the
    // token's number (several of the same token are added up). Add a line for any other effect you want
    // spelled out, e.g. 'damage-boost': 'Deals {n} more damage (Damage Boost)'.
    previewNotes: {
      weakness: 'Deals {n} less damage (Weakness)',
      susceptible: 'Takes {n} more damage (Susceptible)',
    } as Record<string, string>,
    // The Spawn menu: right-click a token to spawn several at once. This is the most in one go.
    spawnManyMax: 20,
    // Troops, Buildings and Emperors arrive with the Shield, Buffer and Thorns counters their sheet row gives
    // them (the shield, buffer and thorns cells in cards.csv). false = players put them on by hand.
    startCounters: true,
    // In a card's close-up: the statuses that make a card inactive. With one on it the card reads
    // "Inactive: Frozen" in place of Active / Tapped / Entrenched. (A Lock on an Emperor locks an ability,
    // not the Emperor, so there it doesn't count.)
    inactiveStatuses: ['frozen', 'stunned', 'locked', 'comatose'] as string[],
    // In a card's close-up, every counter on the card is listed beside it with its number (Thorns 2,
    // Buffer 3, Shield 1…). These are left out of that list, because the close-up tells them another way:
    // damage and SP decay are in the health line, Entrenched is the card's state.
    previewHidesCounters: ['damage', 'sp-decay', 'entrenched'] as string[],
    // A face-up card whose health (a Building's SP) reaches 0 is destroyed: a Destroyed skull token is put on
    // it by the table, which greys it out. It happens once, at the moment it reaches 0: healing it afterwards
    // changes nothing, and if you take the skull off by hand the table leaves it off. The card stays on the
    // table for its owner to discard. `false` = no skull; the cracks and red outline still show.
    destroyAtZero: true,
    // The blue SP Decay counter: the SP a Building has lost by decaying, kept apart from the damage it has
    // taken. It counts against the Building's SP exactly like damage (what is left, what a sacrifice is worth).
    spDecayToken: 'sp-decay',
    // Tokens that may only lie on certain card types (the token goes back where it was, with a message).
    onlyOn: { 'sp-decay': ['building'] } as Record<string, string[]>,
    // Status tokens that some card types shrug off (the token goes back where it was, with a message).
    immune: { building: ['bleed', 'burn', 'regeneration'], emperor: ['possession', 'comatose', 'susceptible'] } as Record<string, string[]>,
    // A token that turns into another one when it lands on a certain card type.
    becomes: { building: { comatose: 'crumble' } } as Record<string, Record<string, string>>,
    // The highest number a counter may show while on a card ("other" = any card type not listed).
    caps: { frozen: { emperor: 2, other: 3 } } as Record<string, Record<string, number>>,
    divinityAlsoRemoves: ['darkgale'], // besides status effects, Divinity clears these off its card
    // Status tokens that come off a card the moment it takes any damage (a Comatose unit wakes up when hit).
    endsOnDamage: ['comatose'] as string[],
    // Counters that keep their whole picture and show their number as a small corner badge,
    // and only when it is above 1 (so a normal 1-turn Entrenched shows no number at all).
    badgeCounters: ['entrenched'] as string[],
    // A token that can carry an unlock cost, in Gerzite or Dark Gerzite. Right-click it to set the cost;
    // the owner of the card it sits on can then pay it to take the token off.
    // The Count counter: how many of the small units inside one card are still standing (Skell-Minis is one
    // card with three skeletons). A card with a number in its `count` cell in cards.csv arrives with one.
    countToken: 'count',
    lockToken: 'locked',
    lockMaxCost: 20,
  },

  // ---------- Discard aura ----------
  // Cards with discard_aura = yes get orbiting lights that scale with their owner's discard pile.
  discardAura: {
    colors: ['#060608', '#b9f5c4', '#8fd0ff', '#4a1c8f'], // black → pale green → light blue → deep purple → back
    cycleSeconds: 9, // time for one full trip through the colors
    fullAt: 20, // discard pile size at which the aura is at full strength
    maxLights: 9,
  },

  // ---------- Cards going to a discard pile ----------
  // A card that leaves the table for a discard pile (a destroyed Troop once it has burst apart, a Spell that has
  // done what it does, a card discarded) flies there face-up, with a soft glow of this color. false = it just goes.
  discardFlight: { enabled: true, glow: '#b8c4d4' },

  // ---------- Card play effects ----------
  // The burst of light when a card lands face-up on the table (played from a hand,
  // or a face-down card flipped face-up). Each rarity has its own effect; change the
  // hex colors here. The first color is the main one, the second is the accent.
  playEffects: {
    // How big crack effects are drawn, against the size of the card (or token) they are on. 0.92 keeps them
    // just inside the edges: sacrifice cracks, a fallen card's cracks, Susceptible, a decaying Building.
    crackScale: 0.92,
    enabled: true,
    colors: {
      common: ['#a3a8b0'],
      uncommon: ['#9be38f'],
      rare: ['#8fcdf5'],
      'ultra-rare': ['#2c58d6', '#86aaff'],
      epic: ['#9a1f34', '#e0566b'],
      mythic: ['#8b3fe0', '#d7b0ff'],
      legendary: ['#f0b22e', '#fff0b0'],
      grand: ['#ffffff', '#ecc35c'],
      ancient: ['#6b0d1d', '#1e6b33'],
      emperor: ['#8fd6ff', '#f4c64e'], // Emperors use this instead of their rarity
      // not rarities, but played the same way:
      tier2: ['#6fe6ff', '#ffffff'], // "powered up" when a card merges to Tier II
      tier3: ['#ff9a3c', '#fff1b8'], // the bigger version for Tier III
      ambush: ['#ffffff', '#9fe8ff'], // swift streaks over a card that arrives with Ambush
      entrance: ['#6ff0d2', '#ffffff', '#c9d2da'], // ability tag "entrance": the card slams down. Shockwave, sparks, and (third color) the dust it kicks up
      remainingJump: ['#b3121f', '#ff4a3d', '#3a0208'], // The Remaining moves within a pile: deep red spirits circle it and rise
      remainingHand: ['#c4121f'], // the glow round The Remaining as it flies to its player's hand (other cards: pileIn)
      // a Calling Pillar's beacon, by how many Pillars its owner has in play (1 = faint … 5 = towering)
      // (light is added to the table, so a red has to be bright to show as deep red: a dark one all but vanishes)
      'aura-pillar-1': ['#d0101c', '#ff3a30'],
      'aura-pillar-2': ['#d8101e', '#ff4034'],
      'aura-pillar-3': ['#e01020', '#ff4638'],
      'aura-pillar-4': ['#e81224', '#ff4e3e'],
      'aura-pillar-5': ['#f0142a', '#ff5a44'],
      'aura-realm': ['#c0121f', '#ff3b30', '#4a0410'], // Realm Beyond Comprehension: the swirls, the sparks, the dark red breath under them
      'recall-swirl': ['#ff6fd2', '#4fb4ff', '#fff0fb'], // Recall Rift: the pink ribbons and glow, the blue ones, the white sparkles and flash
      'aura-bulwark': ['#2f7bff', '#a8d4ff'], // Undead Guard: the blue ward glowing round it, and its paler core
      'aura-veiled': ['#cfe9ff', '#ffffff', '#6fa8c9'], // Hidden Veil: a pale, misty shroud with frost-white glints
      'veil-cast': ['#cfe9ff', '#ffffff', '#6fa8c9'],
      'aura-exit': ['#5fe0c8', '#d6fff6', '#1f8a7a'], // Emergency Exit: a teal water bubble round the Emperor
      'exit-cast': ['#5fe0c8', '#d6fff6', '#1f8a7a'],
      'aura-mapped': ['#d8b46a', '#fff1cc', '#6b4a1e'], // Traveler's Map: a parchment-gold wash and a compass ring over the Biome
      'map-cast': ['#d8b46a', '#fff1cc', '#6b4a1e'],
      'aura-coat': ['#ff7a1a', '#ffd27a', '#7a1a06'], // Molten Coat: molten orange glow, bright embers, dark crust
      'coat-cast': ['#ff7a1a', '#ffd27a', '#7a1a06'],
      'aura-necrosis': ['#7a1a1a', '#9acd32', '#1a0505'], // Burning Necrosis: blood red, sickly green, black rot
      'necrosis-cast': ['#7a1a1a', '#9acd32', '#1a0505'],
      'heat-flare': ['#ff8a2a', '#ffe0a0', '#b3261e'], // Heat Exhaustion: a heat shimmer bursting over the Emperor
      'heat-timer': ['#ff8a2a', '#ffe0a0', '#b3261e'], // …and over the ability timer it lengthened
      'royal-ward-cast': ['#ffcf5a', '#fff2c2', '#b8860b'], // Impervious Shield given: gold glow, pale gold motes, deep gold ring
      'taunt-cast': ['#ff4d2e', '#ffb07a', '#7a1206'], // Taunt cast: the red challenge rings
      'ascend-cast': ['#1b0630', '#9d4dff', '#e2c4ff'], // Arcane Ascension: dark smoke, violet light, pale flash
      'sand-gust': ['#e3c48a', '#fff1cf', '#b08a4e'], // Drifts Tarragon beats its wings: a gust of desert sand blows off it toward the lane in front
      'sand-blind': ['#d9b878', '#f6e6c0', '#9c7a44'], // …and a cloud of stone-powder sand engulfs each Unit there
      'aura-sandblind': ['#d9b878', '#f6e6c0', '#9c7a44'], // …and hangs round it while it may still miss
      'joust-mark': ['#e8edf2', '#ff3b3b'], // Jouster's pick: the steel flash and the red rings
      'aura-royal-ward': ['#ffcf5a', '#fff2c2', '#b8860b'], // Impervious Shield: a soft royal gold glow, pale gold motes, deep gold crest
      'aura-taunt': ['#ff4d2e', '#ffb07a', '#7a1206'], // Taunt: a hot red-orange challenge pulsing out from the Unit
      'aura-ascended': ['#1b0630', '#9d4dff', '#e2c4ff'], // Arcane Ascension: dark smoke, violet arcane light, pale sparks
      'aura-jousted': ['#e8edf2', '#ff3b3b', '#8a1010'], // marked by Jouster: steel-white and red rings closing in on it
      'aura-remaining': ['#e0121f', '#ff5a44', '#050203'], // The Remaining: the red glow breathing round it and its red sparks, brighter red ones, black ones
      pillarsCall: ['#c4121f', '#ff4a3d', '#080203'], // a Pillar in every lane: the eruption on the Emperor
      pillarAnswers: ['#d8142a', '#ff5a44', '#080203'], // …and on each of the Pillars at the same moment
      pillarShatter: ['#e8142a', '#ff6a50'], // each Pillar bursting apart as the call is answered (over the sacrifice effect's cracks)
      remainingArrives: ['#b3121f', '#ff3b30'], // The Remaining is played: the X across Realm Beyond Comprehension
      passiveStopped: ['#b3121f'], // the same pulse while a status has switched the card's Passive off: deep red
      passive: ['#b9a5ff'], // ability tag "passive": a soft pulse every few seconds on its owner's turn
      building: ['#ffc46b', '#fff3d6', '#c9d2da'], // a Building being built as it is played: the frame, its sparks, and (third color) the dust at its foot
      tap: ['#ffb84d', '#fff0c9'], // ability tag "tap": a quick ring when the card is turned sideways
      damage: ['#ff4b3a', '#ffc7a1'], // a damage counter landing on a card, or going up
      statusDamage: ['#ff4b3a', '#ffc7a1'], // the ring on a status token as it deals its damage (Bleed, Decay)
      statusHeal: ['#5fe08a', '#d6ffe0'], // …as it heals (Regeneration)
      statusNotice: ['#ffb53c', '#fff0c9'], // …as it asks you to act (Burn: roll to cure; Crumble: extra SP loss)
      heal: ['#5fe08a', '#d6ffe0'], // healing landing on a card
      link: ['#ffe45c', '#fff3a0'], // the pulse along the Emperor–Minion link when damage is copied across
      trigger: ['#a04dff', '#e3bdff'], // a Trigger card flipped face-up: purple sparks fly in and sink into the card
      triggerSet: ['#a04dff', '#e3bdff'], // a Trigger set face-down: the beam that shoots up as it lands…
      triggerAura: ['#8f3dff', '#e3bdff'], // …and the slim beam that stands on it while it waits
      pileOut: ['#39d7c6', '#d6fff9'], // a card sent straight from one pile to the other: the pile it leaves…
      pileIn: ['#39d7c6', '#d6fff9'], // …and the pile it lands on (teal, unlike anything else on the table)
      freePlay: ['#7fd6ff', '#e6f7ff'], // a card played for free: a small light blue beacon rises from it (the blue of a free play in your hand)
      spDecay: ['#4aa8ff', '#d6ecff'], // a Building decays as its owner's turn ends: blue cracks run over it
      emperorAbility: ['#ffcf5c', '#fff6dc'], // an Emperor uses one of its abilities: a golden surge round the Emperor
      pileLook: ['#ffd98a', '#fff3d6'], // a player looks at the top cards of a pile: the ring and eye, and the ghost cards that lift off it
      // The small scene above an active Biome or Tome, by affiliation (see abilityEffects.ambient below).
      'aura-volcanic': ['#ff8a3c', '#8d8480'], // embers, smoke (a mid grey, so it shows against the dark mat)
      'aura-duned': ['#e0a44e', '#f0d49a'], // sand
      'aura-frigid': ['#ffffff', '#bfe9ff'], // snow, frost glints
      'aura-forested': ['#8fd16a', '#eaf58a'], // leaves, fireflies
      'aura-submerged': ['#4fb8e8', '#d8f4ff'], // ripples, bubbles
      'aura-undead': ['#8fd04f', '#d9f0c0'], // mist (it starts this green and turns black as it rises), motes
      'aura-spectral': ['#9fd8e8', '#cbb8ff'], // wisps, haze
      'aura-magical': ['#e79bff', '#ffe08a'], // sparkles
      'aura-neutral': ['#f4ead2', '#ffffff'], // soft light, glints
      'aura-astral': ['#9a6ad8', '#8c8a98', '#e8deff'], // purple cloud, grey cloud, starlight
      'aura-planets': ['#ffe3b0', '#a77bff', '#6fc3ff', '#d98f5f', '#e8deff'], // an Astral Biome's planetary system: the star, then its planets (violet, blue, rust), and starlight
      // The Epic and Mythic Tomes' own scenes (see abilityEffects.ambient.byCard below).
      'aura-blood-drawing': ['#7a0612', '#d11a2e', '#140003'], // the blood, the drops falling onto it, the darkening round the card
      'aura-forbidden': ['#1a0c26', '#8a3dff', '#c9a6ff'], // the dark wisps, the purple pulse, the pale motes
      'aura-gerzite': ['#8fdcff', '#9fe6ff', '#4fc3ff'], // the gleam, the glints (two light blues)
      'aura-self-destructing': ['#ff7a1a', '#ffd27a', '#5a4a40'], // the heat, the embers, the smoke
      'self-destruct': ['#ff6a00', '#ffc04d', '#4a3c34'], // its timer runs out: the blast (orange), the sparks, the smoke
      'aura-haste': ['#4aa8ff', '#ffb8dc'], // blue streaks, light pink streaks
      'aura-greed': ['#9b4dff', '#a4a4ae', '#050507'], // purple, grey and black sparks
      'aura-moldering': ['#ff3b3b', '#ff9ac8', '#000000'], // red risers (and the red glow), pink risers, the black pulse before them
      'aura-surging': ['#7fd8ff', '#ffffff', '#b98cff'], // the lightning, the sparks, the violet forks
      minion: ['#d6c928', '#fff3a0'], // a troop placed in the Minion slot: the gold of the Minion and Emperor slots
      sacrifice: ['#6e0c20', '#b5142f'], // a unit sacrificed for Dark Gerzite: dark maroon aura, cracks, then it bursts apart
      destroyed: ['#eef2f7', '#8d96a3', '#3b414a'], // a card destroyed (its health gone): pale cracks, then it shatters into grey shards and dust
      darkgale: ['#7a1024', '#ff6a2b'], // a card played as a Darkgale summon: a dark red swell with embers rising off it
      // The Ancients' own effects (see `looks` below and abilityEffects.ambient.byCard)
      'archmage-fire': ['#ff6a1a', '#ffd25a', '#ff2a0a'], // Archmage looks at the opponent's deck: flames dance round it
      'archmage-steam': ['#e8eef2', '#b9c7d0'], // a Submerged card sent to the bottom: steam rising off the deck
      'clay-big': ['#c9a46a', '#8a6a42', '#e8d4a8'], // Clay Soldiers as one big Troop: the big cloud of sand and dust
      'clay-small': ['#c9a46a', '#8a6a42', '#e8d4a8'], // …as four small ones (or a Lanespanner's units): a small puff on each
      cosmic: ['#9fd8ff', '#ffffff', '#b98cff'], // a card Cosmic Beyonder draws, flying to the hand: the glow and its sparkles
      'skulker-pulse': ['#3fb8d8', '#d8f6ff'], // Cragged Skulker gains Damage Boost
      'aura-sarcophagus': ['#ffd25a', '#fff2b8'], // Cursed Sarcophagus: golden glimmers above it
      'burst-gold': ['#b88a14', '#ffd75a'], // …and its burst as it breaks open for the Hexed Mummy (the sacrifice burst, in gold)
      'aura-crosser': ['#9fe0f0', '#c8b0ff', '#e8fbff'], // Eldritch Realm Crosser while it is a Biome: spectral swirls rising
      'flesh-blood': ['#c0101e', '#ff3a3a'], // a Troop sacrificed to the Fleshstealer: the red that runs from it to the Fleshstealer
      'hive-up': ['#7fe060', '#e6ff9a'], // Forest Hivemind gains stats
      'hive-down': ['#b04a2a', '#5a2a18'], // …loses them
      'scarecrow-birds': ['#14161a', '#3a3f48'], // Frozen Scarecrow calls an Icewing: birds circling it
      'burst-gerzite': ['#8a3dff', '#9aa0aa', '#8fdcff'], // Gerzite Titan bursts into its Clusters: purple, grey and light blue
      'golem-reflect': ['#e8cf7a', '#fff3c4', '#c9a65a'], // Great Hay Golem throws damage back: the cloud of straw and the flash
      tornado: ['#cfe9f2', '#7f98a6', '#b9a98c'], // the wind and ring, the darker cloud, the dust kicked up
      'arcane-doors': ['#9fe2ff', '#e8f8ff'], // Arcane Doors: light blue see-through double doors swinging open
      'allstop-pulse': ['#c9a8ff', '#ffffff'], // All Stop: the pulse that spreads out over the table…
      'allstop-burst': ['#c9a8ff', '#ffffff'], // …and each Spell bursting apart as it reaches it
      'gz-shatter': ['#8fe3ff', '#e6fbff'], // Dark Bargain: the Gerzite both players lose rises off the bar and shatters
      'coin-win-magical': ['#b46bff', '#f0dcff'], // Magical Coin won: a purple pulse
      'coin-win-devil': ['#ff2a2a', '#ffb0a0'], // Devil's Coin won: a red pulse
      'coin-shatter': ['#d8d8e0', '#9a9aa8'], // a coin lost: it rises, then shatters like a destroyed card
      'gravity-gust': ['#3c7dff', '#0b0f1e', '#9cc4ff'], // Gravitation: the blue and black wind blowing toward the lane the damage is shifted into
      'timed-blast': ['#7fe6ff', '#c8a0ff', '#ffffff'], // Timed Blast going off over its lane (spectral)
      'crystal-burst': ['#8fe3ff', '#b46bff', '#ffffff'], // Crystalline Discharge: a great light blue and purple ringed burst over each lane
      'gz-charge': ['#8fe3ff', '#d8f6ff'], // Gerzite paid into Gerzite Dagger / Crystalline Discharge: the Gerzite flying in…
      'gz-charge-1': ['#8fe3ff', '#d8f6ff'], // …and the light blue glow round the card, by how much was paid
      'gz-charge-2': ['#7fdcff', '#e6fbff'],
      'gz-charge-3': ['#6fd4ff', '#ffffff'],
      'summit-gust': ['#cfeaff', '#ffffff', '#8fd3ff'], // Summit Tarragon's discount: the wind, the snow, and the icy glint (on the Tarragon as it blows, and on the card it reaches)
      'axe-return': ['#e4e9ee', '#ff3b2f'], // Executioner's axe comes back to its lane: the steel, and the red of its edge
      'assassin-x': ['#e8e8f0', '#ff3a4a'], // Highcard Assassin hits a Legendary, Grand or Ancient: the X slashes
      'aura-leviathan': ['#4fb8e8', '#d8f4ff'], // Leviathan with Body Segments out: the ripple round it
      occult: ['#a04dff', '#e3bdff'], // a card Occult Sorcerer adds to the hand: purple instead of blue as it flies there
      'occult-beam': ['#a04dff', '#e3bdff'], // its Tap discount used: the beam from the Sorcerer to the Spell
      'aura-incinerator': ['#ff6a1a', '#ffc04d', '#5a4a40'], // Primordial Incinerator, untapped: embers and small flames
      'burst-incinerate': ['#ff3a0a', '#ff9a2a'], // a Biome or Tome it burns away: cracks, shakes and crumbles in red and orange
      'soul-take': ['#9fd8e8', '#ffffff'], // a Troop Soul Collector takes from the discard pile: its glow as it flies there, and as it lands
      'souls-in': ['#cfe8ff', '#9fd8e8', '#ffffff'], // Soul Collector collects: souls fly into it from all around
      'bell-flash': ['#0a0c0a', '#b9f5c4'], // The River Keeper: black and pale green light running to its Bell tokens
      'bell-mill': ['#0a0c0a', '#b9f5c4'], // …and the card its Bell knocks off the top of the deck: it flies to the discard pile in pale green
      'bell-ring': ['#b9f5c4', '#0a0c0a'], // a Bell token rings as the opponent discards: it flashes and pulses
      'seedling-grow': ['#c48aff', '#ffffff'], // Twilight Seedling grows at the end of the turn: a pulse
      'aura-marlin-1': ['#3fc4ff', '#d8f6ff'], // Marlin Knight: its glow, 1 … 6 (stronger for each extra hit saved up)
      'aura-marlin-2': ['#3fc4ff', '#d8f6ff'],
      'aura-marlin-3': ['#3fc4ff', '#d8f6ff'],
      'aura-marlin-4': ['#3fc4ff', '#d8f6ff'],
      'aura-marlin-5': ['#3fc4ff', '#d8f6ff'],
      'aura-marlin-6': ['#3fc4ff', '#d8f6ff'],
      'aura-skulker-1': ['#4fb8e8', '#e8f8ff'], // Cragged Skulker's geysers (1 … 8, one for each attack it has gained): water, spray
      'aura-skulker-2': ['#4fb8e8', '#e8f8ff'],
      'aura-skulker-3': ['#4fb8e8', '#e8f8ff'],
      'aura-skulker-4': ['#4fb8e8', '#e8f8ff'],
      'aura-skulker-5': ['#4fb8e8', '#e8f8ff'],
      'aura-skulker-6': ['#4fb8e8', '#e8f8ff'],
      'aura-skulker-7': ['#4fb8e8', '#e8f8ff'],
      'aura-skulker-8': ['#4fb8e8', '#e8f8ff'],
      'aura-seedling-1': ['#b07aff', '#f4eaff', '#e6ff9a'], // its purple and white motes and fireflies, bigger as it grows (1 … 5)
      'aura-seedling-2': ['#b07aff', '#f4eaff', '#e6ff9a'],
      'aura-seedling-3': ['#b07aff', '#f4eaff', '#e6ff9a'],
      'aura-seedling-4': ['#b07aff', '#f4eaff', '#e6ff9a'],
      'aura-seedling-5': ['#b07aff', '#f4eaff', '#e6ff9a'],
      'beast-born': ['#8a3dff', '#5fe06a', '#e3bdff'], // the Twilight Beast arrives: a large purple and green burst
      // The Ancient Spells' and Buildings' own effects
      'blast-arcane': ['#a04dff', '#ff7ad9', '#e3bdff'], // Arcane Explosion goes off: it rises, shakes and blows apart in purple, with pink rings and fragments
      'blast-blood': ['#a0101e', '#ff3a3a', '#3a0208'], // Bloodburst goes off: it bursts into blood, not pieces
      'blood-drain': ['#b0101e', '#ff3a3a'], // health taken from a Troop for Bloodburst: blood drawn out of it
      'enigma-1': ['#c9a6ff', '#ffffff'], // Enigma Beam's beam from the sky on a card: 1 = a little damage … 4 = a lot
      'enigma-2': ['#b07aff', '#ffffff'],
      'enigma-3': ['#9a4dff', '#ffffff'],
      'enigma-4': ['#8a2dff', '#ffffff'],
      'banish-bolt': ['#a04dff', '#e3bdff'], // Banishment's purple lightning striking its caster's deck
      'banish-sparks': ['#a04dff', '#e3bdff'], // …and the opponent's discard pile giving off purple sparks
      'coin-gz': ['#ff6a1a', '#ffd25a'], // Devil's Coin: the fire round the stolen Gerzite
      fiery: ['#ff6a1a', '#ffd25a', '#ff2a0a'], // …and round the cards it makes someone draw
      'aura-vines': ['#3fbf68', '#b4f58a', '#2b8a4c'], // Wingwrap: bright green vines wrapped round each Troop it grounds
      'aura-vines-tangle': ['#2e6b3c', '#7a8f2e', '#1d4f30'], // Tangleweed's Lock: dark seaweed-green vines wrapped round the card's edge
      'overtime-hit': ['#ff7a2e', '#ffb35a'], // Overtime's damage landing on each Emperor as a turn ends: embers
      'overtime-hit-2': ['#ff4a1e', '#ffc06a'], // …in Double Overtime
      'lane-toxic': ['#101410', '#7bd65a'], // Toxic Cloud: little black clouds round its lane's border (a faint green in them)
      'lane-blizzard': ['#cfe8ff', '#ffffff'], // Blizzard: gale winds and snow
      'lane-slag': ['#ff6a1a', '#ffb02e'], // Slag Vial: rocks glowing at the edges, and fire on the floor
      'lane-quake': ['#b4733a', '#5a3a1c'], // Earthquake: small brown cracks and stones (Tectonic Shift's are larger and dark)
      'lane-scald': ['#ff2a2a', '#1a0505'], // Scalding Shadows (in its lane mode): a ring of red and black
      'lane-totem': ['#48e07a', '#ff8fc8'], // Totem of Regeneration: a ring of green and pink
      'lane-surge': ['#3a8cff', '#bfe0ff'], // Energy Surge: a ring of blue, and a blue Ambush flash over it
      'lane-petals': ['#ff9ccf', '#ffd6ec'], // Protective Petals: pink petals falling over the lane it shields
      'lane-smoke': ['#7aa0ff', '#cfdcff'], // Royal Delivery's smoke over its lane (played in the caster's color: these are only a fallback)
      'lane-glow': ['#7aa0ff', '#e2ebff'], // Timed Blast's glow over its lane (likewise)
      'crystal-gz': ['#b07bff', '#f2e6ff'], // a Gerzite Crystallite (or any card) that gives Gerzite as it is destroyed: crystal glints round it as it flies to the bar
      'mine-gz': ['#a04dff', '#e3bdff'], // Gerzite Mine: the glint round the Gerzite flying from it to the bar
      'resurge-bolt': ['#a04dff', '#e3bdff'], // Forbidden Resurgence: the purple lightning striking a Unit
      'burst-resurge': ['#6a1aa8', '#c48aff'], // …which then blows apart in purple
      resurge: ['#8a3dff', '#e3bdff', '#140021'], // …and the card it takes from the discard pile, flying to the hand
      'sand-rise': ['#c9a46a', '#8a6a42', '#e8d4a8'], // Infestation's Sand Crawlers coming up out of the sand
      'glace-sheen': ['#dff4ff', '#ffffff'], // Glace Glass: the sheen across the discard pile, and the white glow of the card flying to the deck
      'spirit-shatter': ['#bfe6ff', '#4fa8ff', '#e8f6ff'], // Undead Legion: an Undead Soldier that falls while its General stands breaks into blue shards…
      'spirit-rise': ['#9fd8ff', '#e6f6ff', '#5fb4ff'], // …and its Spirit Soldier rises out of them like a soul
      'spirit-away': ['#a8dcff', '#e9f7ff'], // a Spirit Soldier fading away as its General falls
      'legion-banner': ['#5b2a9e', '#d8c78f', '#2a1046'], // the General's battle banner: pale gold trim (the cloth and pole take its owner's blue or red on the table; these first and last colors are only a fallback)
      'soul-realm': ['#9fd8e8', '#cbb8ff', '#ffffff'], // Into the Soul Realm goes off: a spectral swell over it
      soul: ['#9fd8e8', '#cbb8ff', '#ffffff'], // …and the card drawn for it
      'shield-pulse': ['#7fd6ff', '#ffffff'], // Persistent Shield adds Overshield: a pulse on the Emperor
      seastorm: ['#2a7fd6', '#d8f4ff', '#ff2a2a', '#ffb0a0'], // Primordial Seastorm: the wave, its foam, the red lightning
      'rift-hole': ['#050208', '#8a3dff', '#3a7fff'], // Spatial Rift: the black hole over each of its lanes, its purple and blue clouds
      'lane-vacuum': ['#07051a', '#b48cff', '#e8f0ff'], // Vacuum: the void over its lane, its violet light and white stars
      'lane-snare': ['#4a3a14', '#d8b46a'], // Cagurn's Thorny Snare: dark thorny vines, and sand
      'intercede-ward': ['#9fe8c8', '#d9c8ff'], // Grave Intercessor's ward over the Troop it chose: pale green, and ghostly violet souls
      'snare-bite': ['#5a3a12', '#ffd27a'], // …springing on a Unit
      'tectonic-sink': ['#5a4a40', '#ff7a2a', '#c9b8a0'], // Tectonic Shift: the ground giving way in each lane
      'tectonic-crack': ['#ff7a2a', '#5a4a40'], // …and the cracks that stay while it is in play
      'voodoo-beam': ['#a04dff', '#e3bdff'], // Voodoo Magic: the beam joining the two Units it links
      'shield-shatter': ['#4fb8ff', '#d8f4ff', '#1f6fa8'], // a Shield counter breaking: it shatters into blue shards
      'templar-charge': ['#d11a2e', '#a04dff', '#5a0010'], // Templar Beam attacks: red and purple sparks drawn in, then a dark red pulse
    } as Record<string, string[]>,
  },

  // ---------- The Ancients' own effects ----------
  // Effects that belong to one card (the recipes are in effects.ts, their colors in playEffects.colors above).
  // The card's effects in cards.csv can also play one with "look <name>" (Cragged Skulker, Twilight Seedling).
  //   arrive        a card that plays its own effect as it lands
  //   handTint      cards drawn into a hand while this card's effect is at work fly there with this glow
  //   summon        played on the card as it summons (Frozen Scarecrow calls an Icewing)
  //   discountBeam  a beam from the card to the Spell its discount was used on
  //   tally         Forest Hivemind's stats going up / down
  //   burst         a card that bursts apart its own way when destroyed (instead of the dark red one)
  // `enabled: false` = none of them play (the cards work the same).
  looks: {
    enabled: true,
    arrive: { 'Twilight Beast': 'beast-born', 'Tribunal Trigger': 'triggerSet' } as Record<string, string>,
    handTint: { 'Occult Sorcerer': 'occult', 'Cosmic Beyonder': 'cosmic', "Devil's Coin": 'fiery', 'Forbidden Resurgence': 'resurge', 'Into the Soul Realm': 'soul' } as Record<string, string>,
    summon: { 'Frozen Scarecrow': 'scarecrow-birds' } as Record<string, string>,
    // markAim       Spells whose light flies to the lanes they mark as they are cast
    // laneBlast     a look played over each of a Spell's lanes as its damage goes off there
    // gerziteCharge Spells paid for with any amount of Gerzite: it flies off the bar into the card, which glows
    //               brighter the more it took (look 'gz-charge')
    // gravityGust   Gravitation shifting damage into another lane: a wind from the card aimed at to that lane
    // sideScreen    a Spell that lays a see-through screen over its caster's side of the mat while it is active
    // cardDome      a Spell that keeps a see-through dome over its own card while it is active (its color)
    markAim: ['Energy Surge', 'Spatial Rift'] as string[],
    laneBlast: { 'Timed Blast': 'timed-blast', 'Crystalline Discharge': 'crystal-burst' } as Record<string, string>,
    gerziteCharge: ['Gerzite Dagger', 'Crystalline Discharge'] as string[],
    gravityGust: 'gravity-gust',
    sideScreen: { 'Light Screen': ['#b26bff', '#e6d0ff'] } as Record<string, string[]>,
    cardDome: { 'Arcane Dome': '#b06cff' } as Record<string, string>,
    discountBeam: { 'Occult Sorcerer': 'occult-beam', 'Summit Tarragon': 'summit-gust' } as Record<string, string>, // (a name ending in -gust blows wind and snow across instead of a beam)
    tally: { up: 'hive-up', down: 'hive-down' },
    burst: { 'Cursed Sarcophagus': 'burst-gold', 'Gerzite Titan': 'burst-gerzite' } as Record<string, string>,
    // The Ancient Spells and Buildings:
    //   noAim        Spells that don't send their affiliation's look at what they hit (they have one of their own)
    //   spellEnd     a Spell that goes out its own way when it is discarded (it rises, shakes and blows apart)
    //   woundLook    played on each of your own Troops a Spell takes health from (Bloodburst: blood drawn out)
    //   strike       on each card the Spell damages, grander the more it deals (name-1 … name-4)
    //   pileStrike   on its caster's deck as it discards the top card
    //   handToPile   the cards it discards from a hand fly together into the discard pile, which then plays this
    //   gainFly      Gerzite it gains (or steals) flies to the bar from the card (or the other bar) with this round it
    //   destroyLook  each card it destroys: `look` on it, and it bursts apart as `burst`
    //   triggered    played on the card when its effect goes off (Into the Soul Realm)
    //   emerge       the Units it summons rise out of the table this way
    //   linkBeam     a beam joining the two cards it links
    //   laneScenes   a scene that stands over each lane chosen for it while it is in play
    noAim: [] as string[], // (none: every Spell's own look plays as well as its affiliation's, never instead of it)
    spellEnd: { 'Arcane Explosion': 'blast-arcane', Bloodburst: 'blast-blood' } as Record<string, string>,
    woundLook: { Bloodburst: 'blood-drain' } as Record<string, string>,
    strike: { 'Enigma Beam': 'enigma' } as Record<string, string>,
    pileStrike: { Banishment: 'banish-bolt' } as Record<string, string>,
    handToPile: { Banishment: 'banish-sparks' } as Record<string, string>,
    gainFly: { 'Gerzite Mine': 'mine-gz', "Devil's Coin": 'coin-gz', 'Passive Income': 'mine-gz' } as Record<string, string>,
    // onTrigger: a look a card plays each time one of its moments sets it off (Arcane Doors: its doors swing open
    // for each card its Spells draw you)
    onTrigger: { 'Arcane Doors': { when: 'ally_spell', look: 'arcane-doors' } } as Record<string, { when: string; look: string }>,
    destroyedGainFly: 'crystal-gz', // Gerzite a card gives as it is destroyed (Gerzite Crystallites) flies from it to the bar ('' = it just appears)
    // how that Gerzite flies: seconds on the way, how high it arcs (cm), and the color of the trail it leaves (by its look)
    gainFlight: { seconds: 1.4, arc: 8, trailSeconds: 0.6, trail: { 'mine-gz': '#a04dff', 'coin-gz': '#ff6a1a', 'crystal-gz': '#c79bff' } as Record<string, string> },
    destroyLook: { 'Forbidden Resurgence': { look: 'resurge-bolt', burst: 'burst-resurge' } } as Record<string, { look: string; burst: string }>,
    triggered: { 'Into the Soul Realm': 'soul-realm' } as Record<string, string>,
    emerge: { Infestation: 'sand', 'Undead Legion': 'sand' } as Record<string, string>, // (Undead Legion's soldiers rise out of the ground the same way)
    linkBeam: { 'Voodoo Magic': 'voodoo-beam' } as Record<string, string>,
    abilityBolts: true, // an Emperor's ability sends its light from the Emperor to each card it reaches, in the Emperor's affiliation (as a Spell does)
    lockVines: ['Tangleweed'] as string[], // a Unit Locked by one of these is wrapped in seaweed vines round its edge for as long as the Lock is on it
    chain: ['Chain Lightning', 'Bouncing Magma Blob'] as string[],
    volley: ['Piercing Arrows', 'Finishing Feast', '1000 Knives'] as string[], // Spells that strike again and again: each strike flies from the Spell on its own, one after another // Spells whose damage jumps from one Unit to the next (each jump sets off from the last Unit it hit)
    laneScenes: {
      'Spatial Rift': 'rift-hole', 'Tectonic Shift': 'tectonic-crack', 'Royal Delivery': 'lane-smoke', 'Timed Blast': 'lane-glow',
      'Toxic Cloud': 'lane-toxic', Blizzard: 'lane-blizzard', 'Slag Vial': 'lane-slag', Earthquake: 'lane-quake', 'Scalding Shadows': 'lane-scald',
      'Totem of Regeneration': 'lane-totem', 'Energy Surge': 'lane-surge', 'Protective Petals': 'lane-petals', Vacuum: 'lane-vacuum',
    } as Record<string, string>,
    laneTinted: ['Royal Delivery', 'Timed Blast'] as string[], // lane scenes played in the caster's color, so both players can see whose lane it is
    biomeScenes: { 'Tectonic Shift': 'tectonic-crack' } as Record<string, string>, // …and one over the Biome slot (Tectonic Shift: no Biome while it is active)
    // a card whose attacks have a look of their own (the styles are in attackFx.ts, their colors in combat.attackFx.colors)
    attackStyle: { 'The Remaining': 'remaining' } as Record<string, string>,
    // a Shield, Overshield or Gerzite Shield counter breaking: it shatters into shards where it sat ('' = nothing)
    shieldBreak: 'shield-shatter',
    // cards a card effect turns up from its player's deck (Occult Sorcerer) are shown to both players, and what happens
    // to them (to the hand, or discarded) waits for that player's Continue. false = it all happens at once
    revealPause: true,
    // The River Keeper: how often (seconds) the light runs from it to its Bell tokens, one after another
    bellEvery: [6, 10] as [number, number],
    // Twilight Seedling's aura grows a step for every this much health it has gained (up to step 5)
    seedlingStep: 4,
  },

  // ---------- Look ----------
  colors: {
    background: '#3a3d43', // medium grey "dark mode" room
    table: '#25282d',
    tableEdge: '#1b1d21',
    cardEdge: '#cfc9bb',
    // the sides of the deck and discard piles: dark like a card back's edge, each card a slightly different
    // grey (base or mid), a lighter line now and then, and a near-black line between cards
    pileEdge: { base: '#1c1d21', mid: '#26282d', light: '#3d4047', gap: '#0d0e10' },
    highlight: '#e3b448', // searching glow, selection, etc.
    gerziteBar: '#4a98cc', // matches the blue gerzite crystals
    darkGerziteBar: '#b01548', // matches the dark red dark gerzite crystals
  },

  // ---------- Backgrounds: the room around the table ----------
  // Each player picks their own under Game Menu → Background. It changes only what THEY see: the room (the
  // air and the floor), never the table or the mat, and never the other player's view. `color` is the room,
  // `floor` the floor (left out, it is the room color a little darker). `group` is the section of the picker
  // it is listed under. To add one, add a line; a new group needs a line in `groups` too.
  // A special environment also has:
  //   sky     a 360° panorama around the table (an equirectangular picture, twice as wide as tall, in
  //           public/rooms/). It replaces the plain room and floor.
  //   skyTurn how far the panorama is turned (degrees), so the part you want is behind the other player
  //   light   the color of the room's light on the table and cards (default white)
  //   motes   floating specks in the air around the table: their color (left out = none)
  backgrounds: {
    default: 'middle',
    groups: [
      { id: 'basic', name: 'Basic' },
      { id: 'special', name: 'Special' },
    ],
    list: [
      { id: 'light', name: 'Light', group: 'basic', color: '#ffffff', floor: '#dcdfe4' },
      { id: 'middle', name: 'Middle', group: 'basic', color: '#3a3d43' }, // the grey room the game started with
      { id: 'dark', name: 'Dark', group: 'basic', color: '#000000', floor: '#08080a' },
      { id: 'emperors-tavern', name: 'Emperor’s Tavern', group: 'special', color: '#452a13', floor: '#5f452e', sky: 'rooms/emperors-tavern.png', skyTurn: 0, light: '#ffd9a8', motes: '#ffc874' },
      { id: 'gerzite-mine', name: 'Gerzite Mine', group: 'special', color: '#34589b', floor: '#282630', sky: 'rooms/themine.png', skyTurn: 0, light: '#4ca6c2', motes: '#ffc874' },
      { id: 'castle', name: 'Castle', group: 'special', color: '#595c3e', floor: '#7a4c24', sky: 'rooms/castle.png', skyTurn: 0, light: '#bdc577', motes: '#ffc874' },
      { id: 'sunken-city', name: 'Sunken City', group: 'special', color: '#4c3ecc', floor: '#051149', sky: 'rooms/sunkencity.png', skyTurn: 0, light: '#293dad', motes: '#ffc874' },
    ] as { id: string; name: string; group: string; color: string; floor?: string; sky?: string; skyTurn?: number; light?: string; motes?: string }[],
  },

  // ---------- Card descriptions ----------
  // statusWords: the status effects a description can name. Each line is a token (its id in tokens.csv) and
  //   the words that mean it. Wherever one of these words appears in a card's text WITH ITS CAPITAL LETTER,
  //   it is shown in a small box in that token's color, on the placeholder card and in the close-up.
  //   To add a status: add its token to tokens.csv, then a line here.
  // labels: words that start a part of a description ("Entrance: …"); they are printed in bold.
  // maxFont / minFont: the description is printed as large as fits its text box, between these sizes
  //   (in pixels on a 500 × 700 card).
  cardText: {
    statusWords: {
      ambush: ['Ambush'],
      bleed: ['Bleed'],
      buffer: ['Buffer'],
      burn: ['Burn', 'Burns', 'Burned'],
      comatose: ['Comatose'],
      crumble: ['Crumble'],
      'damage-boost': ['Damage Boost'],
      decay: ['Decay'],
      divinity: ['Divinity'],
      entrenched: ['Entrenched', 'Entrench', 'Entrenchment'],
      frenzy: ['Frenzy'],
      frozen: ['Frozen', 'Freeze', 'Freezes'],
      locked: ['Locked', 'Lock', 'Locks'],
      overclock: ['Overclock'],
      overheal: ['Overheal'],
      overshield: ['Overshield'],
      possession: ['Possession', 'Possess', 'Possessed', 'Possesses'],
      regeneration: ['Regeneration'],
      shield: ['Shield', 'Shields'],
      stunned: ['Stunned', 'Stun', 'Stuns'],
      susceptible: ['Susceptible', 'Susceptibility'],
      thorns: ['Thorns'],
      weakness: ['Weakness'],
    } as Record<string, string[]>,
    labels: ['Entrance', 'Passive', 'Tap', 'Inherent'] as string[], // (Inherent: like Passive, but nothing turns it off)
    maxFont: 25,
    minFont: 13,
  },

  // ---------- Placeholder cards ----------
  // A card with no picture yet is drawn by the game. Its colors say what it is:
  //   the CARD itself is the color of its rarity,
  //   the TYPE BAR (where it says Troop … Rare) and the TEXT BOX under it are the color of its affiliation
  //   (a card with two affiliations shows both, side by side).
  //   accent: a second color for a rarity, used for its frame, its corners and the name bar
  //           (Grand shares Legendary's color and is told apart by its accent).
  //   emperor: Emperors have no rarity: they get their own card color and accent. Their type bar and text
  //            box are the color of their affiliation, like every other card.
  //   noRarity: for a card with no rarity (summoned units).
  //   noAffiliation: the type bar and text box of a card with no affiliation (Triggers have none).
  // Colors are hex codes. Change one, save, and the placeholders are redrawn.
  placeholderCards: {
    rarity: {
      common: '#525252',
      uncommon: '#447a2c',
      rare: '#2a7e72',
      'ultra-rare': '#173a50',
      epic: '#591b34',
      mythic: '#8e298e',
      legendary: '#6f6b2e',
      grand: '#6f6b2e',
      ancient: '#340716',
    } as Record<string, string>,
    accent: { grand: '#565b2f', ancient: '#101f08' } as Record<string, string>,
    affiliation: {
      neutral: '#5f503c',
      magical: '#6e126c',
      undead: '#424a49',
      spectral: '#568165',
      submerged: '#0a6886',
      volcanic: '#6a290f',
      duned: '#666336',
      frigid: '#4b7878',
      forested: '#155f21',
      astral: '#807087',
    } as Record<string, string>,
    emperor: { main: '#759eb1', accent: '#8d927e' },
    noRarity: '#3b3d44',
    noAffiliation: '#d9d4c7',
  },

  // ---------- Player colors ----------
  // Whoever is picked to go first is blue; the other player is red. (On the solo table you are blue.)
  playerColors: {
    first: '#3f8cff', // blue
    second: '#e5484d', // red
    tableGlow: 0.55, // strength of the colored glow along each player's edge of the table (0 = off)
  },

  // ---------- Dice ----------
  // Six-sided dice. Each die is a row in tokens.csv with category = dice; its color goes here by id.
  dice: {
    size: 2.4, // cm along one edge
    colors: { 'die-blue': '#2f6fe0', 'die-red': '#d23a3a' } as Record<string, string>,
    pips: '#ffffff',
    throwSpeed: 30, // let go while moving faster than this (cm per second) and the die is thrown
    throwTime: 0.35, // a thrown die travels this many seconds' worth of the speed you let go at…
    maxThrow: 55, // …but never farther than this (cm)
  },

  // ---------- Fog over a player's side (the `field_aura` cell in cards.csv) ----------
  // While a card with field_aura = fog or smog is face-up in play, its owner's half of the mat is covered
  // for the OTHER player. The caster never sees it, so their own field stays clear to them.
  // Each kind is a few thin sheets of drifting cloud, one above the other:
  //   color, opacity (how thick: keep it low, the cards must stay readable through it),
  //   scale (bigger = smaller, busier patches), drift (how fast it slides: across, and toward you),
  //   grime: true = small blotchy patches instead of soft banks.
  // height = how far above the table the lowest sheet floats, in cm.
  fieldAuras: {
    // true = the caster sees it too. Handy on the solo table, where there is no opponent to see it for you.
    showToCaster: false,
    kinds: {
      // a pale, cool mist
      fog: {
        height: 2.8,
        sheets: [
          { color: '#e6edf1', opacity: 0.95, scale: 1.0, drift: [0.011, 0.004] },
          { color: '#c9d4db', opacity: 0.8, scale: 1.6, drift: [-0.008, 0.006] },
          { color: '#f4f7f8', opacity: 0.62, scale: 0.75, drift: [0.005, -0.004] },
          { color: '#dfe7ec', opacity: 0.5, scale: 0.5, drift: [-0.004, 0.003] },
        ],
      },
      // the same, but dirty: sour yellow-green banks with dark, sooty patches moving through them
      smog: {
        height: 2.8,
        sheets: [
          { color: '#8f8a3c', opacity: 0.95, scale: 1.0, drift: [0.009, 0.003] },
          { color: '#5a5630', opacity: 0.85, scale: 1.5, drift: [-0.007, 0.005] },
          { color: '#23221a', opacity: 0.75, scale: 2.3, drift: [0.013, -0.006], grime: true },
          { color: '#b4a94a', opacity: 0.5, scale: 0.8, drift: [0.004, -0.003] },
          { color: '#6e6a34', opacity: 0.5, scale: 0.5, drift: [-0.004, 0.003] },
        ],
      },
    } as Record<string, { height: number; sheets: { color: string; opacity: number; scale: number; drift: [number, number]; grime?: boolean }[] }>,
  },

  // ---------- Emperor abilities ----------
  // Each Emperor has up to two abilities, written in data/emperor_abilities.csv (name, cost, cooldown, text).
  // Its owner left-clicks the Emperor and picks one: the cost comes off their bar and a turn timer set to
  // the ability's cooldown is put on the Emperor. The ability can't be used again until that timer is gone.
  // What the ability DOES is carried out by the players, like any card text.
  //   phases: the phases of your own turn an ability can be used in (0 Overdraw, 1 Summon, 2 Attack, 3 End);
  //           [] = any phase. One ability can be given its own time in the `when` column of the sheet:
  //           summon, attack, any_phase, opponent_turn, any_time… (a slash between several)
  //   ownTurnOnly: true = only an ability whose `when` says opponent_turn can be used on the other player's turn
  //   blockedBy: tokens that stop an Emperor using ANY ability while they are on it. Cooldowns keep counting.
  //   A Locked token on an Emperor locks ONE ability: whoever puts it there is asked which. The Emperor's
  //   owner can pay the Lock's cost (right-click it) to take it off. lockSpots: where the two Locks sit.
  //   timer: which timer is the cooldown. 'timer-start' = a Start of Turn Timer: it first counts down as your
  //          NEXT turn begins, so a cooldown of 2 means "not next turn, ready again the turn after"
  //   spots: where on the Emperor the timer of Ability 1, Ability 2 is put (cm from the card's middle;
  //          + z is toward its owner)
  emperorAbilities: {
    enabled: true,
    ownTurnOnly: true,
    phases: [2] as number[],
    blockedBy: ['frozen', 'stunned', 'entrenched', 'destroyed'] as string[],
    timer: 'timer-start',
    spots: [{ x: -1.95, z: 2.9 }, { x: 1.95, z: 2.9 }],
    lockSpots: [{ x: -1.95, z: 0.6 }, { x: 1.95, z: 0.6 }],
  },

  // ---------- Looking at the top cards of a pile ----------
  // Both players see it on the pile: a ring, one ghost card lifting off for each card looked at, and an eye
  // above them. Everything grows with the number of cards.
  //   seconds / secondsPerCard: how long it lasts (1 card: 1.5 s; 10 cards: 2.85 s)
  //   mostCards: no more ghost cards than this, however many are looked at
  //   firstRise / step: how high the first ghost card lifts, and how much higher each next one (cm)
  //   stagger: seconds between one lifting off and the next · fan: how far each is turned from the last
  //   eye: the eye's size for one card · growPerCard: how much bigger it gets per extra card
  //   ringPerCard: how much wider the ring gets per card (cm)
  //   windowDelay: for the player who is looking, the window showing the cards opens this many seconds
  //                (plus `stagger` per card) after the cards start lifting off the pile, so the lift is seen first
  pileLook: { enabled: true, seconds: 1.35, secondsPerCard: 0.15, mostCards: 10, firstRise: 1.4, step: 0.5, stagger: 0.07, fan: 0.1, eye: 4.2, growPerCard: 0.08, ringPerCard: 0.55, windowDelay: 0.5 },

  // ---------- Who a unit can reach ----------
  // Hover over a unit on the table and the lanes it can reach light up: red for the opposing lanes it can
  // attack, green for its own side's lanes it can heal or boost (Blacksmith, Armorsmith, Campfire…).
  //   Emperors, and a troop in the Minion slot, reach every opposing lane. If even one opposing lane is
  //   empty, the opposing Emperor (and Minion, if there is one) light up too.
  //   A troop or building in a lane reaches the opposing lane in front of it, and `range` lanes either side
  //   (its range in cards.csv plus any Range counters on it). A ground unit needs range to hit a lane whose
  //   units are all flying; a card whose text matches `noFlying` never hits them (Cannon). A unit with the
  //   `direct` descriptor also reaches the opposing Emperor and Minion while the lane in front is empty.
  //   A unit with no damage doesn't attack. One with a `support` descriptor reaches its own lane and the
  //   lanes within its range; with no range at all, every lane on its side. `notLanes` = cards that never light anything.
  reach: {
    enabled: true,
    attack: '#ff3b30', // the red of the lanes it can attack
    support: '#4fe08a', // the green of the lanes it can heal or boost
    rangeToken: 'range', // the token whose number adds to a card's range
    direct: 'Direct',
    supportTraits: ['Healing', 'Stat Alter'] as string[],
    noFlying: 'cannot (hit|damage|target|attack) flying',
    notLanes: [341] as number[], // Arcane Library: it boosts Tomes, not units
  },

  // ---------- Card ability effects (the "abilities" column in cards.csv) ----------
  abilityEffects: {
    // A card's one-off effects play one after another, never on top of each other: its rarity effect first,
    // then (for a Trigger flipped face-up) its reveal pulse, then its Entrance effect. This is the pause
    // between one finishing and the next starting, in seconds. Passive pulses wait for all of them too.
    entranceDelay: 0.25,
    // Entrance: a card with this ability doesn't settle onto the table like the others, it drops from `height`
    // (cm) in `fall` seconds, hits the table (shockwave, dust, sparks and a small shake: the 'entrance' recipe
    // in effects.ts) and hops `bounce` cm for `settle` seconds. It plays at the same time as the card's rarity
    // effect. A card flipped face-up on the table rises for `rise` seconds first. `tilt` = how far it leans
    // on the way down. `enabled: false` = the card just lands and the impact effect plays.
    entrance: { enabled: true, height: 10, fall: 0.3, bounce: 0.5, settle: 0.22, rise: 0.22, tilt: 0.22 },
    passiveEvery: 5, // seconds between Passive pulses during the owner's turn
    // While one of these tokens is on a card, the card's own effects stay quiet: its Passive pulse, its Tap
    // effect, its discard aura, and the streaks of an Ambush token on it. (Entrance still plays as it lands.)
    quietedBy: ['frozen', 'stunned', 'comatose', 'entrenched', 'locked'] as string[],
    // Card types that are "built" as they are played face-up from a hand: a frame of light goes up over the
    // card floor by floor, sparks fly as if it were being hammered together, and it settles onto the card.
    // It plays together with the card's rarity effect. `types: []` switches it off.
    built: { types: ['building'] as string[], effect: 'building' },
    revealPulse: { types: ['trigger'], effect: 'trigger' }, // card types that get this effect when flipped face-up, after their rarity effect
    // A face-down card of a type that is always set face-down (arrival.faceDownTypes: Triggers) shows this to
    // both players: `effect` once as it lands, then `loop` for as long as it stays face-down. `enabled: false` = off.
    setAura: { enabled: true, effect: 'triggerSet', loop: 'triggerAura' },
    // Active Biomes and Tomes show a small scene of their affiliation every so often (volcanic: embers and
    // smoke; duned: sand blowing past…), so you can see at a glance that they are still in effect.
    //   cards: which card types get one, and of which rarities ('any' = all). Only the affiliation Tomes
    //          (the ultra-rare ones) have one for now.
    //   every: seconds between one scene ending and the next starting, picked at random in this range.
    //   byCard: cards with a scene of their own instead of their affiliation's, by card number (the Epic and
    //          Mythic Tomes). `effect` = which scene (a recipe in effects.ts, with its colors above), `every` =
    //          its own gap (a negative gap starts the next scene before the last one ends, so it never stops),
    //          and `ends` = a one-off effect played the moment its turns run out (a Destroyed token lands on it).
    //          `when` = only while something is so (see the Ancients' lines). To give another card one, add a
    //          line; to take one away, delete its line.
    // It stops while a Destroyed token is on the card (its turns are over). `enabled: false` = off.
    // How hard a card jolts while a 'rumble' layer plays on it (the Self-Destructing Tome), for a rumble of
    // strength 1: `shift` = cm it is shoved about, `turn` = how far it twists (radians). Its explosion rumbles at 2.4.
    // (It stays flat on the table, so the glow under it never shows through.)
    rumble: { shift: 0.14, turn: 0.022 },
    ambient: {
      enabled: true, effect: 'aura-', every: [5, 9] as [number, number], cards: { biome: 'any', tome: ['ultra-rare'] } as Record<string, 'any' | string[]>,
      byCard: {
        425: { effect: 'aura-pillar-1', every: [-0.9, -0.6] }, // Calling Pillar: a steady deep red beacon (the table swaps in aura-pillar-2…5 as its owner plays more)
        426: { effect: 'aura-realm', every: [-0.5, 0.4] }, // Realm Beyond Comprehension: red swirls and sparks, almost without pause
        1020: { effect: 'aura-planets', every: [-1.0, -0.9] }, // Vast Expanse (the Astral Biome): a small planetary system orbiting above it, without pause
        427: { effect: 'aura-remaining', every: [-0.7, -0.4] }, // The Remaining: a red glow fading in and out round it without pause, and red and black sparks pulled in
        381: { effect: 'aura-blood-drawing', every: [5, 10] }, // Blood-Drawing Tome: blood seeps out now and then
        382: { effect: 'aura-haste', every: [2, 4.5] }, // Haste Tome: blue and pink streaks
        383: { effect: 'aura-moldering', every: [1.5, 3.5] }, // Moldering Tome: a quick black pulse, then red and pink risers
        384: { effect: 'aura-greed', every: [2.5, 5] }, // Tome of Greed: sparks pulled in
        385: { effect: 'aura-forbidden', every: [-1.6, -1.1] }, // Forbidden Tome: dark wisps and a purple pulse, without pause
        386: { effect: 'aura-self-destructing', every: [4, 8], ends: 'self-destruct' }, // Self-Destructing Tome: rumbles, then explodes when its timer hits 0
        387: { effect: 'aura-surging', every: [1.5, 3.5] }, // Surging Power Tome: lightning arcs
        388: { effect: 'aura-gerzite', every: [2.5, 5.5] }, // Tome of Gerzite: light blue glints
        // The Ancients' (`when`: only while 'biome' = it stands as the Biome, 'untapped', 'segments' = its Body
        // Segments are out, 'grows' = its scene grows a step for every CONFIG.looks.seedlingStep health it gains,
        // 'actions' = once it has gained an attacking action, a step for each)
        183: { effect: 'aura-sarcophagus', every: [-1.2, -0.6] }, // Cursed Sarcophagus: golden glimmers above it
        184: { effect: 'aura-crosser', every: [-1.2, -0.8], when: 'biome' }, // Eldritch Realm Crosser as a Biome: spectral swirls rising
        191: { effect: 'aura-leviathan', every: [1.5, 3.5], when: 'segments' }, // Leviathan: water rippling round it
        193: { effect: 'aura-incinerator', every: [-0.9, -0.5], when: 'untapped' }, // Primordial Incinerator: embers and flames
        196: { effect: 'aura-seedling-1', every: [-1.2, -0.8], when: 'grows' },
        126: { effect: 'aura-marlin-1', every: [-0.9, -0.6], when: 'rested' }, // Marlin Knight: a glow that grows with each extra hit it has saved up, gone once it attacks
        182: { effect: 'aura-skulker-1', every: [-0.9, -0.6], when: 'actions' }, // Cragged Skulker: a water geyser round its edge for each attack it has gained // Twilight Seedling: motes and fireflies, more as it grows
      } as Record<number, { effect: string; every?: [number, number]; ends?: string; when?: string }>,
      // a card that guards its lane (always: bulwark — the Undead Guards) glows blue for as long as it stands
      bulwark: { effect: 'aura-bulwark', every: [-0.8, -0.5] as [number, number] },
      // marks on a card that last a while (each a flag on the card's view), the first that applies is shown:
      // Ailleiah's Impervious Shield, Taunt's chosen Unit, Givellt's Arcane Ascension, the Unit Jouster chose
      marks: {
        warded: { effect: 'aura-royal-ward', every: [-1.0, -0.7] as [number, number] },
        taunting: { effect: 'aura-taunt', every: [-0.8, -0.5] as [number, number] },
        ascended: { effect: 'aura-ascended', every: [-1.2, -0.9] as [number, number] },
        jousted: { effect: 'aura-jousted', every: [-0.6, -0.4] as [number, number] },
        blinded: { effect: 'aura-sandblind', every: [-0.9, -0.6] as [number, number] }, // Drifts Tarragon's sand in its eyes
        veiled: { effect: 'aura-veiled', every: [-1.2, -0.9] as [number, number] }, // J'miele's Hidden Veil
        exiting: { effect: 'aura-exit', every: [-1.0, -0.7] as [number, number] }, // Scackle's Emergency Exit
        mapped: { effect: 'aura-mapped', every: [-1.2, -0.9] as [number, number] }, // a Biome Bastian's Traveler's Map has switched off
        coated: { effect: 'aura-coat', every: [-1.0, -0.7] as [number, number] }, // Flamburst's Molten Coat
        necrotic: { effect: 'aura-necrosis', every: [-1.1, -0.8] as [number, number] }, // Barragand's Burning Necrosis
      } as Record<string, { effect: string; every: [number, number] }>,
    },
  },

  // ---------- Camera ----------
  camera: {
    fov: 50,
    moveSpeed: 70, // cm per second for WASD / Q / E
    lookSpeed: 0.004,
    start: { x: 0, y: 95, z: 92, lookAtZ: 22 }, // default seat view
    topDown: { height: 170, z: 14 }, // the Top-Down button's spot
    // The camera can't leave your side of the table:
    bounds: { x: 95, yMin: 8, yMax: 190, zMin: 4, zMax: 125 },
  },
};
